import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiEdit2, FiSend, FiCheckCircle, FiClock, FiPaperclip,
  FiPlay, FiUpload, FiDownload, FiFileText, FiMessageSquare,
  FiAlertCircle, FiCalendar, FiUser,
  FiArrowLeft, FiChevronRight, FiActivity,
  FiX, FiExternalLink, FiPlus,
  FiRefreshCw, FiLayers, FiLock, FiImage, FiSearch, FiEye,
  FiGitBranch, FiGlobe, FiChevronDown, FiCornerUpLeft, FiCornerUpRight
} from 'react-icons/fi';
import { OpinionRequestModal, ForwardActionModal } from '../../components/WorkflowActionModals';
import Lottie from 'lottie-react';
import searchingAnimation from '../../assets/lottie/Searching.json';
import { fetchContract } from '../../store/slices/contractsSlice';
import { workflowsAPI, contractsAPI, documentsAPI, externalReviewAPI, signingAPI, docusignAPI, signatureIntegrationsAPI, usersAPI, parametersAPI, downloadBlob } from '../../services/api';
import { hasPermission, missingPermissionMessage } from '../../utils/permissions';
import { contractStatusKey } from '../../utils/contractStatus';
import { toast } from 'react-toastify';
import { localizeSeeded } from '../FlowManagement/flowUtils';
import { getLocalized } from '../../i18n/getLocalized';
import { differenceInDays } from 'date-fns';
import { formatDateMedium, formatDateTime, formatRelativeTime } from '../../utils/dateFormat';
import { localizeSystemText } from '../../utils/timelineI18n';
import { formatMoney } from '../../utils/numberFormat';
import { sanitizeFieldSchema } from '../../utils/sanitizeSchema';
import { withContractNumber } from '../../utils/documentNaming';
import ContractTimeline from './ContractTimeline';
import ContractOverview from '../../components/ContractOverview/ContractOverview';
import DateField from '../../components/DateField';
import SearchableSelect from '../../components/SearchableSelect';
import ConfirmDialog from '../../components/ConfirmDialog';
import DocumentViewer from '../../components/DocumentViewer';
import './ContractDetail.scss';

// Derive a real file type so documents show proper PDF/Word/… icons.
// Only the signed copy is inherently a PDF; a locked draft keeps its source
// type (the uploaded Word document) — the lock overlay says it is frozen.
function cdDocType(doc) {
  if (doc?.category === 'signed') return { key: 'pdf', ext: 'PDF' };
  const m = (doc?.title || '').match(/\.([a-z0-9]+)$/i);
  const e = m ? m[1].toLowerCase() : '';
  if (e === 'pdf') return { key: 'pdf', ext: 'PDF' };
  if (['doc', 'docx', 'rtf', 'odt'].includes(e)) return { key: 'word', ext: e.toUpperCase() };
  if (['xls', 'xlsx', 'csv', 'ods'].includes(e)) return { key: 'excel', ext: e.toUpperCase() };
  if (['ppt', 'pptx'].includes(e)) return { key: 'ppt', ext: e.toUpperCase() };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(e)) return { key: 'image', ext: e.toUpperCase() };
  if (e) return { key: 'generic', ext: e.slice(0, 4).toUpperCase() };
  // Contract documents are Word docs — both editor-born and uploaded ones
  // (the upload input only accepts .doc/.docx).
  return { key: 'word', ext: 'DOCX' };
}

function CdFileGlyph({ doc }) {
  const { key, ext } = cdDocType(doc);
  const Icon = key === 'image' ? FiImage : FiFileText;
  return (
    <span className={`cd-file cd-file--${key}`} aria-hidden="true">
      <Icon />
      <span className="cd-file__tag">{ext}</span>
    </span>
  );
}

function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { currentContract: contract, versions, comments, approvalProgress, loading, versionInfo } = useSelector((state) => state.contracts);
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role;
  const [workflows, setWorkflows] = useState([]);
  const [showWorkflowPicker, setShowWorkflowPicker] = useState(false);
  const [workflowPickerMode, setWorkflowPickerMode] = useState(null); // 'approval' | 'review'
  const [startingWorkflow, setStartingWorkflow] = useState(false);
  const [wfSearch, setWfSearch] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [contractDocs, setContractDocs] = useState([]);
  const [contractDocsLoading, setContractDocsLoading] = useState(false);
  const [creatingDoc, setCreatingDoc] = useState(false);
  const [draftDragOver, setDraftDragOver] = useState(false);
  const [fieldSchema, setFieldSchema] = useState(null);
  const [viewHistory, setViewHistory] = useState([]);
  const [viewHistoryLoading, setViewHistoryLoading] = useState(false);
  const [docVersions, setDocVersions] = useState({});
  const [docVersionsLoading, setDocVersionsLoading] = useState(false);
  const [uploadingVersionDocId, setUploadingVersionDocId] = useState(null);
  // Which document's version history is expanded in the Contract Documents tab
  const [openVersionsDocId, setOpenVersionsDocId] = useState(null);
  const versionUploadInputRef = useRef(null);
  const versionUploadDocIdRef = useRef(null);
  const signedUploadRef = useRef(null);
  const [showExternalPartyModal, setShowExternalPartyModal] = useState(false);
  const [externalPartyName, setExternalPartyName] = useState('');
  const [externalPartyEmail, setExternalPartyEmail] = useState('');
  const [sendingExternalInvite, setSendingExternalInvite] = useState(false);

  // ── Approval action state ──
  const [myPendingApproval, setMyPendingApproval] = useState(null);
  const [approvalActionLoading, setApprovalActionLoading] = useState(false);
  // Görüş Al / Yönlendir — per-step abilities of the active action owner,
  // plus the contract's opinion records for the Görüşler tab.
  const [showOpinionModal, setShowOpinionModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [opinions, setOpinions] = useState([]);
  // The opinion giver answers in the Görüş Bildir popup.
  const [showGiveOpinionModal, setShowGiveOpinionModal] = useState(false);
  const [opinionDraft, setOpinionDraft] = useState('');
  const [opinionSubmitting, setOpinionSubmitting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [changesComment, setChangesComment] = useState('');
  // Signature-stage return to revision (initiator only)
  const [showReturnRevisionModal, setShowReturnRevisionModal] = useState(false);
  const [returnRevisionReason, setReturnRevisionReason] = useState('');
  const [requestEditLoading, setRequestEditLoading] = useState(false);

  // ── Contract Update & Version Management state ──
  const [showUpdateMenu, setShowUpdateMenu] = useState(false);
  // 'amendment' | 'addendum' | 'responsible' | 'supporting' | null
  const [updateModal, setUpdateModal] = useState(null);
  const [updateChangeNote, setUpdateChangeNote] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  // Responsible-person picker inside the update modal
  const [respQuery, setRespQuery] = useState('');
  const [respResults, setRespResults] = useState([]);
  const [respSelected, setRespSelected] = useState(null); // { _id, name, email }
  // Files staged in the "Add Supporting Document" modal — each row carries the
  // file plus its classification ({ file, docTypeId, expiresAt }).
  const [supportingFiles, setSupportingFiles] = useState([]);
  // Admin-defined supporting document types (active only) for the pickers.
  const [supportingDocTypes, setSupportingDocTypes] = useState([]);
  // Attachments-tab upload staging: the chosen file waits for its type and
  // expiry date in a small dialog before the actual upload.
  const [attachStage, setAttachStage] = useState(null); // { file, docTypeId, expiresAt }
  const [attachUploading, setAttachUploading] = useState(false);
  // Major versions of this contract's family (Versions tab)
  const [contractVersions, setContractVersions] = useState([]);
  const [contractVersionsLoading, setContractVersionsLoading] = useState(false);
  // Header version switcher (replaces the old Versions tab).
  const [showVersionMenu, setShowVersionMenu] = useState(false);
  const versionMenuRef = useRef(null);

  // ── Signing state ──
  const [showSignDropdown, setShowSignDropdown] = useState(false);
  // Signing-date editor at the signed-contract step (null = untouched draft)
  const [signDateDraft, setSignDateDraft] = useState(null);
  const [savingSignDate, setSavingSignDate] = useState(false);
  const [showSignWizard, setShowSignWizard] = useState(false);
  const [showDocuSignModal, setShowDocuSignModal] = useState(false);
  const [hasDocuSign, setHasDocuSign] = useState(false);
  const [sendingSigning, setSendingSigning] = useState(false);
  const [uploadingSigned, setUploadingSigned] = useState(false);
  // Signed-copy file waiting for the user's confirmation before upload
  const [confirmSignedFile, setConfirmSignedFile] = useState(null);
  // QR verification failed on the signed upload — failure details plus the
  // rejected file ({ status, detail, matchedContract, file }); the user can
  // continue by entering a mandatory override reason.
  const [qrFailure, setQrFailure] = useState(null);
  const [qrOverrideReason, setQrOverrideReason] = useState('');
  // In-app read-only viewer target ({ docId, title, version, signed, locked })
  const [viewerTarget, setViewerTarget] = useState(null);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [activeSignerIndex, setActiveSignerIndex] = useState(null);
  const [signingStatus, setSigningStatus] = useState(null);
  // Wizard state
  const [wizardStep, setWizardStep] = useState(0); // 0=select docs, 1..N=signers per doc, N+1=review
  const [lockedDocs, setLockedDocs] = useState([]);
  const [lockedDocsLoading, setLockedDocsLoading] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [docSignerMap, setDocSignerMap] = useState({}); // { docId: { signers: [...], signingOrder: 'parallel' } }
  const [activeWizardDocIndex, setActiveWizardDocIndex] = useState(0);
  // DocuSign modal state
  const [dsTemplates, setDsTemplates] = useState([]);
  const [dsTemplatesLoading, setDsTemplatesLoading] = useState(false);
  const [dsSelectedTemplate, setDsSelectedTemplate] = useState(null);
  const [dsSigners, setDsSigners] = useState([]);
  const [dsSending, setDsSending] = useState(false);

  // Document names carry the contract number wherever they are shown, for the
  // same reason downloads do: the same template title appears on any number of
  // contracts, and the number is what tells two of them apart.
  const docDisplayName = (title) => withContractNumber(title, contract?.contractNumber);

  // One rule for every download button: an unlocked document downloads its
  // working .docx version; once it is locked (cover page + QR generated) every
  // download serves that frozen final PDF — the same file the viewer shows —
  // named with the _LockVersion suffix. Both end with the contract number, so
  // same-named documents from different contracts stay apart on disk.
  const handleDownloadDocVersion = (doc, versionNumber) => {
    const locked = doc.isLocked;
    const url = locked
      ? documentsAPI.downloadPdfUrl(id, doc._id)
      : documentsAPI.downloadVersion(id, doc._id, versionNumber);
    // L: shared axios instance — honours the impersonation/delegation token
    // store and the refresh interceptor, unlike a raw fetch + localStorage.
    downloadBlob(url)
      .then((res) => {
        const blobUrl = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = locked
          ? `${withContractNumber(`${doc.title}_LockVersion`, contract?.contractNumber)}.pdf`
          : `${withContractNumber(`${doc.title}_v${versionNumber}`, contract?.contractNumber)}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error(t('contractDetail.failedToDownloadVersion')));
  };

  const triggerVersionUpload = (docId) => {
    versionUploadDocIdRef.current = docId;
    if (versionUploadInputRef.current) {
      versionUploadInputRef.current.value = '';
      versionUploadInputRef.current.click();
    }
  };

  const handleVersionFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const docId = versionUploadDocIdRef.current;
    if (!file || !docId) return;

    setUploadingVersionDocId(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await documentsAPI.uploadVersion(id, docId, formData);

      // Refresh just this document's version list
      const { data } = await documentsAPI.getVersions(id, docId);
      setDocVersions((prev) => ({
        ...prev,
        [docId]: { title: prev[docId]?.title || '', versions: data.versions || [] },
      }));
      toast.success(t('contractDetail.versionUploaded'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.failedToUploadVersion'));
    } finally {
      setUploadingVersionDocId(null);
      versionUploadDocIdRef.current = null;
    }
  };

  useEffect(() => { dispatch(fetchContract(id)); }, [dispatch, id]);

  // Active supporting-document types feed the upload dialogs' Type pickers.
  // Inactive types are excluded here on purpose: existing documents keep
  // showing their (populated) type, but new uploads can't select it.
  useEffect(() => {
    parametersAPI.supportingDocTypes.list({ active: 'true' })
      .then(({ data }) => setSupportingDocTypes(data.items || []))
      .catch(() => setSupportingDocTypes([]));
  }, [id]);

  // One-shot deep link: the contract form lands here right after the flow was
  // sent, asking for the Timeline tab so the starter sees the process kick
  // off. The state is cleared immediately — refreshes and later visits open
  // the detail on its normal default tab.
  useEffect(() => {
    const requestedTab = location.state?.openTab;
    // Returning from Version Compare additionally asks for that document's
    // version history to be open, so the user lands back on the list they
    // launched the comparison from rather than a collapsed Documents tab.
    const requestedVersionsDoc = location.state?.openVersionsDocId;
    if (requestedTab || requestedVersionsDoc) {
      if (requestedTab) setActiveTab(requestedTab);
      if (requestedVersionsDoc) setOpenVersionsDocId(requestedVersionsDoc);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);
  useEffect(() => {
    workflowsAPI.list().then(({ data }) => setWorkflows(data.workflows || [])).catch(() => {});
  }, []);

  // The approver whose Control step gates on the draft is here to attach it —
  // open the Documents tab for them instead of making them find it. Fires once,
  // and never over a tab the user (or a deep link) already chose.
  const draftGateTabRef = useRef(false);
  useEffect(() => {
    if (draftGateTabRef.current || contractDocsLoading || activeTab !== 'overview') return;
    if (myPendingApproval?.reviewActions?.canAddDocuments !== true) return;
    if (contractDocs.some((d) => d.category !== 'signed' && d.category !== 'supporting')) return;
    draftGateTabRef.current = true;
    setActiveTab('documents');
  }, [myPendingApproval, contractDocs, contractDocsLoading, activeTab]);

  // Drafts have no detail view — they open straight in the edit screen.
  useEffect(() => {
    if (contract && contract._id === id && contract.status === 'draft') {
      navigate(`/contracts/${id}/edit`, { replace: true });
    }
  }, [contract, id, navigate]);

  // Record that the current user opened this contract, then load the full
  // view history (who opened it and when) for the "Views" tab.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const loadHistory = () => {
      setViewHistoryLoading(true);
      contractsAPI.viewHistory(id)
        .then(({ data }) => { if (!cancelled) setViewHistory(data.views || []); })
        .catch(() => { if (!cancelled) setViewHistory([]); })
        .finally(() => { if (!cancelled) setViewHistoryLoading(false); });
    };
    contractsAPI.recordView(id).catch(() => {}).finally(loadHistory);
    return () => { cancelled = true; };
  }, [id]);

  // Fetch pending approval for current user on this contract
  useEffect(() => {
    if (!contract || !['pending_approval', 'in_review'].includes(contract.status)) {
      setMyPendingApproval(null);
      return;
    }
    workflowsAPI.pendingApprovals()
      .then(({ data }) => {
        const match = (data.approvals || []).find(a => a.contractId?._id === id || a.contractId === id);
        setMyPendingApproval(match || null);
      })
      .catch(() => setMyPendingApproval(null));
  }, [contract, id]);

  // Opinions (Görüş Al) recorded on this contract — shown in their own tab.
  const loadOpinions = () => {
    workflowsAPI.contractOpinions(id)
      .then(({ data }) => setOpinions(data.opinions || []))
      .catch(() => setOpinions([]));
  };
  useEffect(loadOpinions, [id]);

  // Yorumlar + Görüşler in one feed: every opinion request shows as a
  // "Görüş İstendi" entry (with its note), every answer as "Görüş Verildi" —
  // no separate tab. Newest first, like the comment list itself.
  const commentFeed = useMemo(() => {
    const items = (comments || []).map((c) => ({ kind: 'comment', time: c.createdAt, c }));
    for (const o of opinions) {
      items.push({ kind: 'opinion_request', time: o.createdAt, o });
      if (o.status === 'answered') {
        items.push({ kind: 'opinion_answer', time: o.respondedAt || o.updatedAt, o });
      }
    }
    return items.sort((a, b) => new Date(b.time) - new Date(a.time));
  }, [comments, opinions]);

  // An opinion requested from the current user and still open — they answer
  // inline on the Görüşler tab.
  const myPendingOpinion = opinions.find(
    (o) => o.status === 'pending' && String(o.assignee?._id) === String(user?._id)
  ) || null;

  // Görüş Al document grants — abilities the requester switched on for this
  // opinion giver, alive only while the request is pending. Additive to the
  // normal gates and never part of the user's permanent permissions.
  const opinionCanUploadVersions = myPendingOpinion?.grants?.canUploadVersions === true;
  const opinionCanAddSupporting = myPendingOpinion?.grants?.canAddSupportingDocs === true;

  const handleSubmitOpinion = async (opinionRecord) => {
    if (!opinionDraft.trim()) {
      toast.error(t('approvals.messages.opinionTextRequired'));
      return;
    }
    setOpinionSubmitting(true);
    try {
      await workflowsAPI.respondOpinion(opinionRecord._id, { opinion: opinionDraft.trim() });
      toast.success(t('approvals.messages.opinionSubmitted'));
      setOpinionDraft('');
      setShowGiveOpinionModal(false);
      loadOpinions();
      // The answer lands in the Yorumlar feed as a "Görüş Verildi" entry —
      // show it, so the submitter sees where their opinion went.
      setActiveTab('comments');
    } catch (err) {
      const code = err.response?.data?.code;
      toast.error(code === 'OPINION_NOT_ACTIVE'
        ? t('workflowActions.opinion.errNotActive')
        : err.response?.data?.error || t('workflowActions.opinion.failed'));
    }
    setOpinionSubmitting(false);
  };

  useEffect(() => {
    setContractDocsLoading(true);
    documentsAPI.list(id)
      .then(({ data }) => setContractDocs(data.documents || []))
      .catch(() => setContractDocs([]))
      .finally(() => setContractDocsLoading(false));
  }, [id]);

  // Fetch document versions for all contract documents
  useEffect(() => {
    if (!contractDocs.length) return;
    setDocVersionsLoading(true);
    Promise.all(
      contractDocs.map((doc) =>
        documentsAPI.getVersions(id, doc._id)
          .then(({ data }) => ({ docId: doc._id, title: doc.title, versions: data.versions || [] }))
          .catch(() => ({ docId: doc._id, title: doc.title, versions: [] }))
      )
    ).then((results) => {
      const map = {};
      results.forEach((r) => { map[r.docId] = { title: r.title, versions: r.versions }; });
      setDocVersions(map);
    }).finally(() => setDocVersionsLoading(false));
  }, [contractDocs, id]);

  useEffect(() => {
    const contractTypeId = contract?.contractTypeId?._id || contract?.contractTypeId;
    if (!contractTypeId) return;
    import('../../services/api').then(({ parametersAPI }) => {
      parametersAPI.contractTypes.getConfig(contractTypeId)
        .then(res => setFieldSchema(sanitizeFieldSchema(res.data.fieldSchema) || null))
        .catch(() => setFieldSchema(null));
    });
  }, [contract?.contractTypeId]);

  // Check if DocuSign is linked for this tenant
  useEffect(() => {
    signatureIntegrationsAPI.list()
      .then(({ data }) => {
        const linked = (data.integrations || []).some(i => i.status === 'linked');
        setHasDocuSign(linked);
      })
      .catch(() => setHasDocuSign(false));
  }, []);

  // Fetch signing status for the contract
  useEffect(() => {
    if (!id) return;
    signingAPI.status(id)
      .then(({ data }) => setSigningStatus(data.signingRequest))
      .catch(() => setSigningStatus(null));
  }, [id]);

  // User search for signer selection
  useEffect(() => {
    if (!userSearchQuery || userSearchQuery.length < 2) {
      setUserSearchResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      usersAPI.search(userSearchQuery, 5)
        .then(({ data }) => setUserSearchResults(data.users || []))
        .catch(() => setUserSearchResults([]));
    }, 300);
    return () => clearTimeout(timeout);
  }, [userSearchQuery]);

  // User search for the Responsible Person Update modal
  useEffect(() => {
    if (!respQuery || respQuery.length < 2) {
      setRespResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      usersAPI.search(respQuery, 8)
        .then(({ data }) => setRespResults(data.users || []))
        .catch(() => setRespResults([]));
    }, 300);
    return () => clearTimeout(timeout);
  }, [respQuery]);

  // Major versions of the contract family — powers the header version
  // switcher, which only appears once an amendment/addendum has been created
  // (count > 1).
  useEffect(() => {
    if (!versionInfo || (versionInfo.count || 1) <= 1) {
      setContractVersions([]);
      return;
    }
    let cancelled = false;
    setContractVersionsLoading(true);
    contractsAPI.versions(id)
      .then(({ data }) => { if (!cancelled) setContractVersions(data.versions || []); })
      .catch(() => { if (!cancelled) setContractVersions([]); })
      .finally(() => { if (!cancelled) setContractVersionsLoading(false); });
    return () => { cancelled = true; };
  }, [id, versionInfo]);

  // Close the header version menu on any click outside of it.
  useEffect(() => {
    if (!showVersionMenu) return;
    const onDown = (e) => {
      if (versionMenuRef.current && !versionMenuRef.current.contains(e.target)) setShowVersionMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showVersionMenu]);

  // ── Contract Update & Version Management handlers ──
  const openUpdateModal = (type) => {
    setShowUpdateMenu(false);
    setUpdateChangeNote('');
    setRespQuery('');
    setRespResults([]);
    setRespSelected(null);
    setSupportingFiles([]);
    setUpdateModal(type);
  };

  // Amendment / Addendum: clone the contract into a new pending version and
  // jump straight into its edit form — the workflow is then re-run from there.
  const handleCreateVersion = async () => {
    if (!updateChangeNote.trim()) {
      toast.error(t('contractDetail.changeNoteRequired', { defaultValue: 'Please describe the change.' }));
      return;
    }
    setUpdateLoading(true);
    try {
      const { data } = await contractsAPI.createVersion(id, {
        updateType: updateModal,
        changeDescription: updateChangeNote.trim(),
      });
      toast.success(t('contractDetail.versionCreated', {
        defaultValue: 'New contract version {{number}} created',
        number: data.contract.contractNumber,
      }));
      setUpdateModal(null);
      navigate(`/contracts/${data.contract._id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.versionCreateFailed', { defaultValue: 'Failed to create the new version' }));
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleResponsibleUpdate = async () => {
    if (!respSelected?._id) {
      toast.error(t('contractDetail.selectNewResponsible', { defaultValue: 'Please select the new responsible person.' }));
      return;
    }
    if (!updateChangeNote.trim()) {
      toast.error(t('contractDetail.changeNoteRequired', { defaultValue: 'Please describe the change.' }));
      return;
    }
    setUpdateLoading(true);
    try {
      await contractsAPI.updateResponsible(id, {
        responsibleUserId: respSelected._id,
        changeDescription: updateChangeNote.trim(),
      });
      toast.success(t('contractDetail.responsibleUpdated', { defaultValue: 'Responsible person updated' }));
      setUpdateModal(null);
      dispatch(fetchContract(id));
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.responsibleUpdateFailed', { defaultValue: 'Failed to update the responsible person' }));
    } finally {
      setUpdateLoading(false);
    }
  };

  // Add Supporting Document: appends to the contract's attachments; existing
  // documents are untouched, no new version, no workflow restart. Each staged
  // row carries its own type and expiry date.
  const handleSupportingUpload = async () => {
    if (!supportingFiles.length) {
      toast.error(t('contractDetail.selectSupportingFiles', { defaultValue: 'Please select at least one file.' }));
      return;
    }
    if (supportingDocTypes.length && supportingFiles.some((row) => !row.docTypeId)) {
      toast.error(t('contractDetail.supportingTypeRequired', { defaultValue: 'Please select the supporting document type.' }));
      return;
    }
    setUpdateLoading(true);
    try {
      for (const row of supportingFiles) {
        const formData = new FormData();
        formData.append('document', row.file);
        if (row.docTypeId) formData.append('docTypeId', row.docTypeId);
        if (row.expiresAt) formData.append('expiresAt', row.expiresAt);
        await contractsAPI.upload(id, formData);
      }
      toast.success(t('contractDetail.supportingDocsAdded', {
        defaultValue: '{{count}} supporting document(s) added',
        count: supportingFiles.length,
      }));
      setUpdateModal(null);
      dispatch(fetchContract(id));
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.supportingDocsAddFailed', { defaultValue: 'Failed to add supporting documents' }));
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleSignNormal = () => {
    setShowSignDropdown(false);
    setWizardStep(0);
    setSelectedDocIds([]);
    setDocSignerMap({});
    setActiveWizardDocIndex(0);
    setShowSignWizard(true);
    setLockedDocsLoading(true);
    signingAPI.getLockedDocuments(id)
      .then(({ data }) => setLockedDocs(data.documents || []))
      .catch(() => { toast.error(t('contractDetail.failedToLoadLockedDocs')); setLockedDocs([]); })
      .finally(() => setLockedDocsLoading(false));
  };

  const handleSignDocuSign = () => {
    setShowSignDropdown(false);
    setDsTemplates([]);
    setDsSelectedTemplate(null);
    setDsSigners([]);
    setShowDocuSignModal(true);
    setDsTemplatesLoading(true);
    docusignAPI.listTemplates()
      .then(({ data }) => setDsTemplates(data.templates || []))
      .catch(() => toast.error(t('contractDetail.failedToLoadDocuSignTemplates')))
      .finally(() => setDsTemplatesLoading(false));
  };

  // Offline signing — upload an already-signed PDF and mark the contract executed.
  const handleUploadSignedClick = () => {
    setShowSignDropdown(false);
    signedUploadRef.current?.click();
  };

  const handleSignedFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error(t('contractDetail.signedMustBePdf', { defaultValue: 'The signed document must be a PDF.' }));
      return;
    }
    setConfirmSignedFile(file);
  };

  const performSignedUpload = async (file, overrideReason = '') => {
    if (!file) return;
    setUploadingSigned(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (overrideReason) fd.append('overrideReason', overrideReason);
      await signingAPI.uploadSigned(id, fd);
      toast.success(t('contractDetail.markedExecuted', { defaultValue: 'Signed copy uploaded. Contract marked as executed.' }));
      setConfirmSignedFile(null);
      setQrFailure(null);
      setQrOverrideReason('');
      dispatch(fetchContract(id));
      signingAPI.status(id).then(({ data }) => setSigningStatus(data.signingRequest)).catch(() => {});
      documentsAPI.list(id).then(({ data }) => setContractDocs(data.documents || [])).catch(() => {});
    } catch (err) {
      // The QR in the uploaded copy didn't match the approved draft. Keep the
      // file and ask for an override reason — entering one lets the user
      // continue the upload despite the failed verification.
      if (err.response?.status === 422 && err.response.data?.code === 'QR_VERIFICATION_FAILED') {
        setQrFailure({ ...(err.response.data.qrVerification || {}), file });
        setQrOverrideReason('');
        setConfirmSignedFile(null);
      } else {
        toast.error(err.response?.data?.error || t('contractDetail.failedToUploadSigned', { defaultValue: 'Failed to upload the signed copy.' }));
        setConfirmSignedFile(null);
      }
    } finally {
      setUploadingSigned(false);
    }
  };

  const handleConfirmSignedUpload = () => performSignedUpload(confirmSignedFile);

  // Wizard: toggle document selection
  const handleToggleDoc = (docId) => {
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(d => d !== docId) : [...prev, docId]
    );
  };

  // Wizard: proceed from document selection to signer assignment
  const handleWizardNextFromDocs = () => {
    if (selectedDocIds.length === 0) { toast.error(t('contractDetail.selectAtLeastOneDoc')); return; }
    // Initialize signer map for new selections
    const newMap = { ...docSignerMap };
    selectedDocIds.forEach(docId => {
      if (!newMap[docId]) {
        newMap[docId] = { signers: [{ name: '', email: '', userId: null, order: 0 }], signingOrder: 'parallel' };
      }
    });
    // Remove deselected docs
    Object.keys(newMap).forEach(docId => {
      if (!selectedDocIds.includes(docId)) delete newMap[docId];
    });
    setDocSignerMap(newMap);
    setActiveWizardDocIndex(0);
    setWizardStep(1);
  };

  // Wizard: signer manipulation for current document
  const currentWizardDocId = selectedDocIds[activeWizardDocIndex] || null;
  const currentDocSigners = currentWizardDocId ? (docSignerMap[currentWizardDocId]?.signers || []) : [];
  const currentDocOrder = currentWizardDocId ? (docSignerMap[currentWizardDocId]?.signingOrder || 'parallel') : 'parallel';

  const handleWizardAddSigner = () => {
    if (!currentWizardDocId) return;
    setDocSignerMap(prev => ({
      ...prev,
      [currentWizardDocId]: {
        ...prev[currentWizardDocId],
        signers: [...(prev[currentWizardDocId]?.signers || []), { name: '', email: '', userId: null, order: (prev[currentWizardDocId]?.signers || []).length }],
      },
    }));
  };

  const handleWizardRemoveSigner = (idx) => {
    if (!currentWizardDocId) return;
    setDocSignerMap(prev => ({
      ...prev,
      [currentWizardDocId]: {
        ...prev[currentWizardDocId],
        signers: prev[currentWizardDocId].signers.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })),
      },
    }));
  };

  const handleWizardSignerChange = (idx, field, value) => {
    if (!currentWizardDocId) return;
    setDocSignerMap(prev => ({
      ...prev,
      [currentWizardDocId]: {
        ...prev[currentWizardDocId],
        signers: prev[currentWizardDocId].signers.map((s, i) => i === idx ? { ...s, [field]: value } : s),
      },
    }));
  };

  const handleWizardSigningOrderChange = (value) => {
    if (!currentWizardDocId) return;
    setDocSignerMap(prev => ({
      ...prev,
      [currentWizardDocId]: { ...prev[currentWizardDocId], signingOrder: value },
    }));
  };

  const handleSelectUser = (idx, u) => {
    if (!currentWizardDocId) return;
    setDocSignerMap(prev => ({
      ...prev,
      [currentWizardDocId]: {
        ...prev[currentWizardDocId],
        signers: prev[currentWizardDocId].signers.map((s, i) => i === idx ? {
          ...s,
          name: `${u.firstName} ${u.lastName}`.trim(),
          email: u.email,
          userId: u._id,
        } : s),
      },
    }));
    setUserSearchQuery('');
    setUserSearchResults([]);
    setActiveSignerIndex(null);
  };

  // Wizard: navigation between doc signer steps
  const handleWizardNextDoc = () => {
    const valid = currentDocSigners.every(s => s.name.trim() && s.email.trim());
    if (!valid) { toast.error(t('contractDetail.allSignersMustHaveNameEmail')); return; }
    if (activeWizardDocIndex < selectedDocIds.length - 1) {
      setActiveWizardDocIndex(prev => prev + 1);
      setActiveSignerIndex(null);
      setUserSearchQuery('');
    } else {
      // Move to review step
      setWizardStep(2);
    }
  };

  const handleWizardPrevDoc = () => {
    if (activeWizardDocIndex > 0) {
      setActiveWizardDocIndex(prev => prev - 1);
      setActiveSignerIndex(null);
      setUserSearchQuery('');
    } else {
      setWizardStep(0);
    }
  };

  const getDocTitle = (docId) => {
    const doc = lockedDocs.find(d => d._id === docId);
    return doc?.title || t('contractDetail.untitledContract');
  };

  const handleSubmitNormalSigning = async () => {
    // Validate all documents have at least one signer
    for (const docId of selectedDocIds) {
      const entry = docSignerMap[docId];
      if (!entry || entry.signers.length === 0) {
        toast.error(t('contractDetail.docMustHaveAtLeastOneSigner', { title: getDocTitle(docId) }));
        return;
      }
      const valid = entry.signers.every(s => s.name.trim() && s.email.trim());
      if (!valid) {
        toast.error(t('contractDetail.allSignersForDocMust', { title: getDocTitle(docId) }));
        return;
      }
    }
    setSendingSigning(true);
    try {
      const { data } = await signingAPI.initiate({
        contractId: id,
        method: 'normal',
        documents: selectedDocIds.map(docId => ({
          documentId: docId,
          signers: docSignerMap[docId].signers.map((s, i) => ({ name: s.name, email: s.email, userId: s.userId, order: i })),
          signingOrder: docSignerMap[docId].signingOrder,
        })),
      });
      if (data?.emailWarnings?.length) {
        toast.warn(t('contractDetail.signingEmailFailed', {
          defaultValue: 'Invite email could not be sent to: {{emails}}',
          emails: data.emailWarnings.map(w => w.email).join(', '),
        }), { autoClose: 10000 });
      } else {
        toast.success(t('contractDetail.signingInvitationsSent'));
      }
      setShowSignWizard(false);
      dispatch(fetchContract(id));
      signingAPI.status(id).then(({ data }) => setSigningStatus(data.signingRequest)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.failedToInitiateSigning'));
    } finally {
      setSendingSigning(false);
    }
  };

  const handleSelectDsTemplate = async (template) => {
    setDsSelectedTemplate(template);
    // If roles are already populated from the list, use them
    if (template.roles && template.roles.length > 0) {
      setDsSigners(template.roles.map(r => ({ roleName: r.roleName, name: r.name || '', email: r.email || '' })));
      return;
    }
    // Otherwise fetch full template detail to get signer roles
    try {
      const { data } = await docusignAPI.getTemplate(template.templateId);
      const roles = data.roles || [];
      setDsSelectedTemplate({ ...template, roles });
      setDsSigners(roles.map(r => ({ roleName: r.roleName, name: r.name || '', email: r.email || '' })));
      if (roles.length === 0) {
        toast.error(t('contractDetail.noSignerRolesConfigured'));
      }
    } catch {
      toast.error(t('contractDetail.failedToLoadTemplateDetails'));
    }
  };

  const handleDsSignerChange = (idx, field, value) => {
    setDsSigners(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const handleSubmitDocuSign = async () => {
    const valid = dsSigners.every(s => s.name.trim() && s.email.trim());
    if (!valid) { toast.error(t('contractDetail.allSignerFieldsMustBeFilled')); return; }
    setDsSending(true);
    try {
      await docusignAPI.send({
        contractId: id,
        templateId: dsSelectedTemplate.templateId,
        templateName: dsSelectedTemplate.name,
        signers: dsSigners,
      });
      toast.success(t('contractDetail.docuSignEnvelopeSent'));
      setShowDocuSignModal(false);
      dispatch(fetchContract(id));
      signingAPI.status(id).then(({ data }) => setSigningStatus(data.signingRequest)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.failedToSendDocuSign'));
    } finally {
      setDsSending(false);
    }
  };

  const handleCheckDocuSignStatus = async () => {
    if (!signingStatus?._id) return;
    try {
      const { data } = await docusignAPI.checkStatus(signingStatus._id);
      if (data.status === 'completed') {
        toast.success(t('contractDetail.allSignaturesComplete'));
        setSigningStatus(null);
        dispatch(fetchContract(id));
      } else if (data.status === 'declined' || data.status === 'voided') {
        toast.error(t('contractDetail.envelopeWas', { status: data.status }));
        setSigningStatus(null);
        dispatch(fetchContract(id));
      } else {
        toast.info(t('contractDetail.docuSignStatus', { status: data.status }));
        signingAPI.status(id).then(({ data: d }) => setSigningStatus(d.signingRequest)).catch(() => {});
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.failedToCheckDocuSignStatus'));
    }
  };

  const handleCancelSigning = async () => {
    if (!signingStatus?._id) return;
    if (!window.confirm(t('contractDetail.cancelSigningConfirm'))) return;
    try {
      await signingAPI.cancel(signingStatus._id);
      toast.success(t('contractDetail.signingRequestCancelled'));
      setSigningStatus(null);
      dispatch(fetchContract(id));
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.failedToCancelSigning'));
    }
  };

  if (loading || !contract) {
    return (
      <div className="cd-loading">
        <div className="cd-loading__spinner" />
        <span>{t('contractDetail.loadingContract')}</span>
      </div>
    );
  }

  const formatDate = (d) => formatDateMedium(d);
  const formatRelative = (d) => formatRelativeTime(d);

  // ── Version-history field diff helpers ──────────────────────────────
  const DATE_CHANGE_FIELDS = new Set(['contractDate', 'startDate', 'endDate', 'signingDate']);

  const humanizeKey = (key) => (key || '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (m) => m.toUpperCase());

  const changeFieldLabel = (c) => {
    // Table cell change → "Section · Row N · Column".
    if (c.group === 'table') {
      const parts = [];
      if (c.label) parts.push(c.label);
      if (c.row) parts.push(t('contractDetail.versionRow', { n: c.row, defaultValue: 'Row {{n}}' }));
      if (c.column) parts.push(c.column);
      return parts.join(' · ') || t('contractDetail.versionFields.status', { defaultValue: 'Table' });
    }
    // Resolved schema label (custom field) → use it directly.
    if (c.label) return c.label;
    if (c.group === 'fieldData') return humanizeKey(c.field);
    return t(`contractDetail.versionFields.${c.field}`, { defaultValue: c.field });
  };

  const formatChangeValue = (field, val) => {
    if (val === null || val === undefined || val === '') return '—';
    if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
    if (typeof val === 'boolean' || field === 'autoRenewal') {
      return (val === true || val === 'true') ? t('common.yes') : t('common.no');
    }
    if (field === 'priority') return t(`contractForm.priorities.${val}`, { defaultValue: String(val) });
    if (field === 'confidentialityLevel') return t(`contractForm.confidentialityLevels.${val}`, { defaultValue: String(val) });
    if (DATE_CHANGE_FIELDS.has(field)) {
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? String(val) : formatDateMedium(d);
    }
    if (field === 'contractAmount') {
      return formatMoney(val, String(val));
    }
    return String(val);
  };

  const getDaysUntilExpiry = () => {
    if (!contract.endDate) return null;
    return differenceInDays(new Date(contract.endDate), new Date());
  };

  const daysUntilExpiry = getDaysUntilExpiry();
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;

  const handleStartWorkflow = async (workflowId) => {
    setStartingWorkflow(true);
    try {
      const payload = { workflowId };
      if (workflowPickerMode === 'review') {
        payload.stepType = 'review';
      } else if (workflowPickerMode === 'approval') {
        payload.stepType = 'approval';
      }
      await workflowsAPI.start(id, payload);
      toast.success(
        workflowPickerMode === 'review'
          ? t('contractDetail.submittedForReview')
          : t('contractDetail.workflowStarted')
      );
      setShowWorkflowPicker(false);
      setWorkflowPickerMode(null);
      dispatch(fetchContract(id));
      showTimelineAfterAction();
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.code === 'VALUE_OUT_OF_RANGE'
        ? t('flows.messages.valueOutOfRange', {
          amount: formatMoney(data.details?.amount),
          currency: data.details?.currency,
          min: data.details?.minValue != null ? formatMoney(data.details.minValue) : '—',
          max: data.details?.maxValue != null ? formatMoney(data.details.maxValue) : '—',
        })
        : data?.code === 'ORG_ASSIGNEE_UNRESOLVED'
        ? t('flows.messages.orgAssigneeUnresolved', { step: data.stepName })
        : missingPermissionMessage(data, t, i18n.language)
          || data?.error || t('errors.failedToSave'));
    } finally {
      setStartingWorkflow(false);
    }
  };

  // After a decision the active tab jumps to the Timeline, so the user sees
  // their action land in the process history without switching manually.
  const showTimelineAfterAction = () => setActiveTab('timeline');

  const handleApprove = async () => {
    if (!myPendingApproval) return;
    setApprovalActionLoading(true);
    try {
      const wasReview = myPendingApproval.stepType === 'review';
      await workflowsAPI.approve(myPendingApproval._id, {});
      toast.success(wasReview
        ? t('contractDetail.markedReviewed')
        : t('contractDetail.contractApproved'));
      setMyPendingApproval(null);
      dispatch(fetchContract(id));
      showTimelineAfterAction();
    } catch (err) {
      const data = err.response?.data;
      toast.error(missingPermissionMessage(data, t, i18n.language)
        || (data?.code === 'SIGNED_DOC_REQUIRED'
          ? t('flows.messages.signedDocRequired')
          : data?.code === 'DRAFT_DOC_REQUIRED_STEP'
          ? t('flows.messages.draftDocRequiredStep')
          : data?.code === 'OPINIONS_PENDING'
          ? t('flows.messages.opinionsPending')
          : data?.code === 'NOT_STEP_ASSIGNEE'
          ? t('flows.messages.notStepAssignee')
          : data?.error || t('contractDetail.failedToApprove')));
    } finally {
      setApprovalActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!myPendingApproval) return;
    setApprovalActionLoading(true);
    try {
      const wasControlGate = myPendingApproval.stepType === 'control';
      await workflowsAPI.reject(myPendingApproval._id, { comments: rejectComment });
      // The two endings read differently: a control gate hands the contract
      // back to the initiator, an approval step closes it.
      toast.success(wasControlGate
        ? t('contractDetail.contractNotSuitable')
        : t('contractDetail.contractRejected'));
      setMyPendingApproval(null);
      setShowRejectModal(false);
      setRejectComment('');
      dispatch(fetchContract(id));
      showTimelineAfterAction();
    } catch (err) {
      const data = err.response?.data;
      toast.error(missingPermissionMessage(data, t, i18n.language)
        || (data?.code === 'NOT_STEP_ASSIGNEE'
          ? t('flows.messages.notStepAssignee')
          : data?.error || t('contractDetail.failedToReject')));
    } finally {
      setApprovalActionLoading(false);
    }
  };

  // Third approval decision, neither approving nor rejecting: it ends this run
  // and returns the contract to the person who started it, as 'pending_edit'.
  const handleRequestChanges = async () => {
    if (!myPendingApproval) return;
    if (!changesComment.trim()) {
      toast.error(t('contractDetail.changesReasonRequired'));
      return;
    }
    setApprovalActionLoading(true);
    try {
      await workflowsAPI.requestChanges(myPendingApproval._id, { comments: changesComment.trim() });
      toast.success(t('contractDetail.changesRequested'));
      setMyPendingApproval(null);
      setShowChangesModal(false);
      setChangesComment('');
      dispatch(fetchContract(id));
      showTimelineAfterAction();
    } catch (err) {
      const data = err.response?.data;
      toast.error(missingPermissionMessage(data, t, i18n.language)
        || (data?.code === 'NOT_STEP_ASSIGNEE'
          ? t('flows.messages.notStepAssignee')
          : data?.error || t('contractDetail.failedToRequestChanges')));
    } finally {
      setApprovalActionLoading(false);
    }
  };

  // Signature-stage counterpart of request-changes: the initiator pulls the
  // contract back for revision instead of signing it. Lands in the same
  // 'pending_edit' state, so the edit + resend tooling below applies as is.
  const handleReturnToRevision = async () => {
    if (!myPendingApproval) return;
    if (!returnRevisionReason.trim()) {
      toast.error(t('contractDetail.returnToRevisionReasonRequired'));
      return;
    }
    setApprovalActionLoading(true);
    try {
      await workflowsAPI.returnToRevision(myPendingApproval._id, { comments: returnRevisionReason.trim() });
      toast.success(t('contractDetail.returnedToRevision'));
      setMyPendingApproval(null);
      setShowReturnRevisionModal(false);
      setReturnRevisionReason('');
      dispatch(fetchContract(id));
      showTimelineAfterAction();
    } catch (err) {
      const data = err.response?.data;
      toast.error(missingPermissionMessage(data, t, i18n.language)
        || (data?.code === 'NOT_WORKFLOW_INITIATOR'
          ? t('contractDetail.returnToRevisionNotInitiator')
          : data?.error || t('contractDetail.failedToReturnToRevision')));
    } finally {
      setApprovalActionLoading(false);
    }
  };

  // The other half: the initiator has made the edits and sends the contract
  // again. The workflow it was running restarts from its first step — the
  // approvals given before the changes were given against an older contract.
  const handleResend = async () => {
    const req = contract?.changeRequest;
    if (!req?.workflowId) return;
    setApprovalActionLoading(true);
    try {
      await workflowsAPI.start(id, {
        workflowId: req.workflowId,
        ...(req.stepTypeFilter ? { stepType: req.stepTypeFilter } : {}),
      });
      toast.success(t('contractDetail.resent'));
      dispatch(fetchContract(id));
      showTimelineAfterAction();
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.code === 'VALUE_OUT_OF_RANGE'
        ? t('flows.messages.valueOutOfRange', {
          amount: formatMoney(data.details?.amount),
          currency: data.details?.currency,
          min: data.details?.minValue != null ? formatMoney(data.details.minValue) : '—',
          max: data.details?.maxValue != null ? formatMoney(data.details.maxValue) : '—',
        })
        : data?.code === 'CONTRACT_FIELDS_MISSING'
        ? t('contractDetail.missingFieldsOnResend')
        : data?.code === 'ORG_ASSIGNEE_UNRESOLVED'
          ? t('flows.messages.orgAssigneeUnresolved', { step: data.stepName })
          : missingPermissionMessage(data, t, i18n.language)
            || data?.error || t('errors.failedToSave'));
    } finally {
      setApprovalActionLoading(false);
    }
  };

  // The third thing the holder can do with a returned contract: neither edit
  // nor resend it, but let it go. The open request is cleared and the contract
  // is a draft again — nothing is deleted, so it can still be started later.
  const handleCancelChangeRequest = async () => {
    if (!window.confirm(t('contractDetail.cancelChangeRequestConfirm'))) return;
    setApprovalActionLoading(true);
    try {
      await workflowsAPI.cancelChangeRequest(id);
      toast.success(t('contractDetail.changeRequestCancelled'));
      dispatch(fetchContract(id));
      showTimelineAfterAction();
    } catch (err) {
      const data = err.response?.data;
      toast.error(missingPermissionMessage(data, t, i18n.language)
        || data?.error || t('contractDetail.failedToCancelChangeRequest'));
    } finally {
      setApprovalActionLoading(false);
    }
  };

  const handleRequestEdit = async () => {
    if (!window.confirm(t('contractDetail.requestEditConfirm'))) return;
    setRequestEditLoading(true);
    try {
      await contractsAPI.requestEdit(id);
      toast.success(t('contractDetail.contractReopened'));
      dispatch(fetchContract(id));
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || t('contractDetail.failedToRequestEdit'));
    } finally {
      setRequestEditLoading(false);
    }
  };

  const openWorkflowPicker = (mode) => {
    if (contractDocs.length === 0) {
      toast.error(mode === 'approval' ? t('contractDetail.addDocBeforeApproval') : t('contractDetail.addDocBeforeReview'));
      return;
    }
    setWorkflowPickerMode(mode);
    setShowWorkflowPicker(true);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      await contractsAPI.addComment(id, { content: newComment });
      setNewComment('');
      dispatch(fetchContract(id));
      toast.success(t('contractDetail.commentAdded'));
    } catch (err) {
      toast.error(t('errors.failedToSave'));
    } finally {
      setSubmittingComment(false);
    }
  };

  // Supporting attachments carry a type and an expiry date — the chosen file
  // is staged and the dialog collects both before the actual upload.
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAttachStage({ file, docTypeId: '', expiresAt: '' });
    e.target.value = '';
  };

  const handleAttachStageUpload = async () => {
    if (!attachStage?.file) return;
    // The type is mandatory whenever types are defined; a tenant with no
    // types yet can still upload plain files.
    if (supportingDocTypes.length && !attachStage.docTypeId) {
      toast.error(t('contractDetail.supportingTypeRequired', { defaultValue: 'Please select the supporting document type.' }));
      return;
    }
    setAttachUploading(true);
    const formData = new FormData();
    formData.append('document', attachStage.file);
    if (attachStage.docTypeId) formData.append('docTypeId', attachStage.docTypeId);
    if (attachStage.expiresAt) formData.append('expiresAt', attachStage.expiresAt);
    try {
      await contractsAPI.upload(id, formData);
      toast.success(t('contractDetail.documentUploaded', { name: attachStage.file.name }));
      setAttachStage(null);
      dispatch(fetchContract(id));
    } catch (err) {
      const data = err.response?.data;
      toast.error(missingPermissionMessage(data, t, i18n.language)
        || data?.error || t('errors.failedToUpload'));
    } finally {
      setAttachUploading(false);
    }
  };

  const handleDownload = (doc) => {
    const url = contractsAPI.downloadUrl(id, doc._id);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', doc.originalName);
    // L: shared axios instance — honours the impersonation/delegation token
    // store and the refresh interceptor, unlike a raw fetch + localStorage.
    downloadBlob(url)
      .then(res => {
        const blobUrl = window.URL.createObjectURL(res.data);
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error(t('errors.failedToDownload')));
  };

  const handleSendToExternalParty = async () => {
    if (!externalPartyName.trim() || !externalPartyEmail.trim()) {
      toast.error(t('contractDetail.pleaseEnterNameAndEmail'));
      return;
    }
    setSendingExternalInvite(true);
    try {
      await externalReviewAPI.invite({
        contractId: id,
        externalPartyName: externalPartyName.trim(),
        externalPartyEmail: externalPartyEmail.trim(),
      });
      toast.success(t('contractDetail.invitationSent', { name: externalPartyName.trim() }));
      setShowExternalPartyModal(false);
      setExternalPartyName('');
      setExternalPartyEmail('');
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.failedToSendInvitation'));
    } finally {
      setSendingExternalInvite(false);
    }
  };

  const isContractCreator = contract?.createdBy === user?._id || contract?.createdBy?._id === user?._id;

  const effectivePerms = user?.effectivePermissions;
  const canEdit = hasPermission(userRole, 'edit_contract', effectivePerms);
  const canUpload = hasPermission(userRole, 'upload_documents', effectivePerms);
  // submit_review was folded into create_contract: creating a contract covers
  // sending it into its review/approval workflow.
  const canSubmitReview = hasPermission(userRole, 'create_contract', effectivePerms);
  const canDownload = hasPermission(userRole, 'download_documents', effectivePerms);
  // 'rejected' — an approval step refused the contract and the process closed.
  // Read-only like the other closed states.
  const isImmutable = ['pending_approval', 'approved', 'fully_executed', 'archived', 'terminated', 'sent_for_signature', 'partially_signed', 'rejected'].includes(contract.status);

  // The process ended without the contract ever completing: refused at an
  // approval step ("Reddedildi"), or cancelled/terminated afterwards. These
  // are dead ends by design — the record is kept for reading and reporting,
  // and no action that would move the process forward is offered on it.
  // Contracts that completed normally are untouched by this.
  const processClosed = ['rejected', 'terminated'].includes(contract.status);

  // ── Contract Update & Version Management flags ──
  // Superseded versions are frozen history: view-only, no actions at all.
  const isSuperseded = contract.versionState === 'superseded';
  const isVersionContract = ['amendment', 'addendum'].includes(contract.versionType);
  // "Approval process completed" — the only states the Update menu opens in.
  const approvalCompleted = ['approved', 'fully_executed', 'active', 'expiring_soon', 'renewed'].includes(contract.status);
  // Update menu (Sözleşme Güncelleme). Each entry carries its own permission;
  // the menu itself only opens when at least one of them is granted, so a user
  // never faces an empty dropdown.
  const canAmend = hasPermission(userRole, 'update_contract_amendment', effectivePerms);
  const canAddendum = hasPermission(userRole, 'update_contract_addendum', effectivePerms);
  const canUpdateResponsible = hasPermission(userRole, 'update_contract_responsible', effectivePerms);
  const canAddSupporting = hasPermission(userRole, 'add_supporting_documents', effectivePerms);
  const canUpdateContract = canEdit && approvalCompleted && !isSuperseded
    && (canAmend || canAddendum || canUpdateResponsible || canAddSupporting);
  const hasMajorVersions = (versionInfo?.count || 1) > 1;
  const currentFamilyVersion = contractVersions.find((v) => v.versionState === 'current');

  // Request Edit reopens a *completed* process. A run that ended in a
  // rejection (or was cancelled) is closed: the contract stays readable —
  // overview, documents, timeline, history — but nothing on it may start the
  // process again, so no action is offered here at all.
  const canRequestEdit = canEdit
    && contract.status === 'approved'
    && !isSuperseded
    && !(signingStatus && ['in_progress', 'pending'].includes(signingStatus.status));

  // Reviewer step restrictions — the flow step assigned to the current user
  // carries an action list (what the reviewer may do on this contract).
  // No pending approval, or a legacy approval without the snapshot → unrestricted.
  const reviewerActions = myPendingApproval?.reviewActions || null;
  // The final signed-contract step is completed by signing/uploading the
  // signed copy — it gets the sign actions, not the approve/reject pair.
  const isSignedStepTurn = myPendingApproval?.stepSystemKey === 'signed_contract';
  // A review step offers one process action ("Reviewed") — never approve/reject.
  const isReviewStepTurn = myPendingApproval?.stepType === 'review';
  // A control (Kontrol) step offers Uygun Görüldü / Uygun Görülmedi — pass the
  // gate or end the run; never request-changes.
  const isControlStepTurn = myPendingApproval?.stepType === 'control';
  // Harici Gönder — a per-step ability like Görüş Al and Yönlendir. The step
  // that is running right now decides whether its owner may send the contract
  // outside the system; with the designer switch off the action is not shown
  // at all. myPendingApproval is already the caller's *active* step, so no
  // further ordering check is needed here (the server re-checks anyway).
  const canSendExternalFromStep = myPendingApproval?.allowExternalSend === true;
  // Sent back for edits. The contract is the initiator's again: they get the
  // edit action (pending_edit is not an immutable status) and the resend that
  // starts the workflow over.
  const isPendingEdit = contract.status === 'pending_edit';
  const changeRequest = contract.changeRequest?.requestedAt ? contract.changeRequest : null;
  // Only the person it was handed to — the workflow initiator — resends it.
  // Falls back to the creator for contracts returned before assignedTo existed.
  const holderId = changeRequest?.assignedTo?._id || changeRequest?.assignedTo;
  const isChangeHolder = holderId ? String(holderId) === String(user?._id) : isContractCreator;
  const canResend = isPendingEdit && isChangeHolder && !!changeRequest?.workflowId;
  // A step that found the contract not suitable ("Uygun Görülmedi") sends it
  // back the same way a change request does — the banner and the toast say
  // which of the two it was.
  const wasRejected = isPendingEdit && contract.approvalState === 'rejected';
  // The holder's other option: drop it. The contract becomes a draft again.
  const canCancelChangeRequest = isPendingEdit && isChangeHolder && !!changeRequest;
  // Harici Gönder, second level: the flow's "Geri Gönderimde Başlatana Harici
  // Gönder İzni" switch, snapshotted onto the change request when the run sent
  // the contract back. Only the holder, only while it is back with them.
  const canSendExternalOnReturn = isPendingEdit && isChangeHolder
    && changeRequest?.allowExternalSend === true;
  const reviewerAllows = (key) => !reviewerActions || reviewerActions[key] === true;
  const canDownloadNow = canDownload && reviewerAllows('canDownloadDocuments');
  // pending_approval normally locks the contract, but the flow designer can
  // grant the current step's holder specific actions for the duration of
  // their step — mirror the backend's grant-aware locks: the action passes
  // the reviewer gate AND either the contract isn't locked, or it's locked
  // only by pending_approval and this step explicitly grants the action.
  // A contract that was sent back belongs to exactly one person: whoever it
  // was handed to. Everybody else reads it — including approvers whose review
  // step granted them document actions during the round that has now ended,
  // and anyone who simply holds edit_contract. Their step is over; offering
  // them Düzenle invites edits nobody asked for on a contract that is not
  // theirs.
  const returnedToSomebodyElse = isPendingEdit && !isChangeHolder;
  const allowsUnlocked = (key) => reviewerAllows(key)
    && !returnedToSomebodyElse
    && (!isImmutable || (contract.status === 'pending_approval' && reviewerActions?.[key] === true));

  // Editor entry points. Opening the editor only ever ends in a new document
  // version, so the editor is offered exactly where the backend would accept
  // one — allowsUnlocked, not reviewerAllows: once the contract is sent for
  // approval (or signed/executed/archived) no version can be added, so the
  // read-only viewer takes over.
  const canEditDocumentsNow = allowsUnlocked('canEditDocuments');

  // Signed-contract step: the flow opens the signing date to the initiator so
  // the signature details can be completed without reopening the contract.
  const canEditSigningDateNow = isSignedStepTurn
    && reviewerActions?.canEditSigningDate === true
    && contract.status === 'pending_approval';
  const currentSigningDate = contract.signingDate ? String(contract.signingDate).split('T')[0] : '';
  const handleSaveSigningDate = async () => {
    if (signDateDraft == null || signDateDraft === currentSigningDate) return;
    setSavingSignDate(true);
    try {
      await contractsAPI.update(id, {
        signingDate: signDateDraft,
        changeDescription: 'Signing date updated at the signed-contract step',
      });
      toast.success(t('contractDetail.signingDateSaved'));
      setSignDateDraft(null);
      dispatch(fetchContract(id));
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || t('contractDetail.signingDateSaveFailed'));
    } finally {
      setSavingSignDate(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: { color: '#64748b', bg: 'rgba(100,116,139,0.1)', icon: FiEdit2, label: t('contracts.status.draft') },
      in_review: { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', icon: FiClock, label: t('contracts.status.in_review') },
      review_completed: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', icon: FiCheckCircle, label: t('contracts.status.review_completed') },
      pending_approval: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: FiClock, label: t('contracts.status.pending_approval') },
      // Two names for one stored status — which one depends on the decision
      // that sent the contract back (see utils/contractStatus).
      sent_back: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: FiCornerUpLeft, label: t('contracts.status.sent_back') },
      not_suitable: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: FiX, label: t('contracts.status.not_suitable') },
      approved: { color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: FiCheckCircle, label: t('contracts.status.approved') },
      sent_for_signature: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', icon: FiEdit2, label: t('contracts.status.sent_for_signature') },
      active: { color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: FiCheckCircle, label: t('contracts.status.active') },
      fully_executed: { color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: FiCheckCircle, label: t('contracts.status.fully_executed') },
      terminated: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: FiX, label: t('contracts.status.terminated') },
      archived: { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', icon: FiLayers, label: t('contracts.status.archived') },
      expiring_soon: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: FiAlertCircle, label: t('contracts.status.expiring_soon') },
      rejected: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: FiX, label: t('contracts.status.rejected') },
    };
    return configs[status] || { color: '#64748b', bg: 'rgba(100,116,139,0.1)', icon: FiFileText, label: status };
  };

  const statusConfig = getStatusConfig(contractStatusKey(contract));
  const StatusIcon = statusConfig.icon;
  const approvalPercent = approvalProgress?.total
    ? Math.round((approvalProgress.completed / approvalProgress.total) * 100)
    : 0;

  // Draft upload is a single action everywhere it is offered — pick (or drop)
  // the file and it is in. The title is derived from the file name, exactly
  // as the contract form's Taslak Sözleşme field does at the first stage; no
  // mode chooser, no title field, no confirm button in between.
  const createContractDoc = async (file) => {
    setCreatingDoc(true);
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('title', file.name.replace(/\.[^/.]+$/, '').trim() || t('contractDetail.draftContract'));
        formData.append('file', file);
        res = await documentsAPI.create(id, formData);
      } else {
        res = await documentsAPI.create(id, { title: contract?.title?.trim() || t('contractDetail.draftContract') });
      }
      const newDoc = res.data.document;
      toast.success(t('contractDetail.documentCreated', { title: newDoc.title }));
      // The card (and its v1 row — docVersions refetches off contractDocs)
      // appears immediately; the list call only reconciles.
      setContractDocs((prev) => [newDoc, ...prev.filter((d) => d._id !== newDoc._id)]);
      documentsAPI.list(id).then(({ data }) => setContractDocs(data.documents || [])).catch(() => {});
      // An uploaded draft is done — it lands in the list and nobody is thrown
      // into the editor. A blank one is empty, so open it to start writing.
      if (!file) navigate(`/contracts/${id}/documents/${newDoc._id}/editor`, { state: { from: 'contract' } });
    } catch (err) {
      toast.error(err.response?.data?.code === 'DRAFT_EXISTS'
        ? t('contractDetail.draftExists')
        : (err.response?.data?.error || t('contractDetail.failedToCreateDocument')));
    } finally {
      setCreatingDoc(false);
    }
  };

  const handleDraftFilePicked = (file) => {
    if (!file || creatingDoc) return;
    if (!/\.(docx?|DOCX?)$/.test(file.name)) {
      toast.error(t('contractDetail.draftFileTypeInvalid'));
      return;
    }
    createContractDoc(file);
  };

  // One contract = one draft contract document. Once the draft slot is taken
  // (any non-signed document), New Document is hidden — changes go in as new
  // versions of the existing draft.
  const hasDraftDoc = contractDocs.some((d) => d.category !== 'signed' && d.category !== 'supporting');

  const tabs = [
    { id: 'overview', label: t('contractDetail.overview'), icon: <FiActivity size={15} /> },
    { id: 'documents', label: t('contractDetail.contractDocuments'), count: contractDocs.length || 0, icon: <FiFileText size={15} /> },
    { id: 'attachments', label: t('contractDetail.attachments'), count: contract.documents?.length || 0, icon: <FiPaperclip size={15} /> },
    // Major versions live in the hero's version switcher, not in a tab — the
    // Turkish tab labels were crowding the tab bar, and "which version am I
    // looking at" should be visible at all times anyway.
    // Yorumlar carries the Görüş entries too (Görüş İstendi / Görüş Verildi)
    // — no separate opinions tab.
    { id: 'comments', label: t('contractDetail.comments'), count: commentFeed.length, icon: <FiMessageSquare size={15} /> },
    { id: 'timeline', label: t('contractDetail.timeline'), icon: <FiRefreshCw size={15} /> },
    { id: 'history', label: t('contractDetail.versions'), count: versions?.length || 0, icon: <FiClock size={15} /> },
    { id: 'views', label: t('contractDetail.viewsTab'), count: viewHistory.length || 0, icon: <FiEye size={15} /> },
  ];

  // Priority config
  const priorityConfig = {
    low: { color: '#10b981', bg: 'rgba(16,185,129,0.1)', label: t('contractForm.priorities.low') },
    medium: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: t('contractForm.priorities.medium') },
    high: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: t('contractForm.priorities.high') },
    critical: { color: '#dc2626', bg: 'rgba(220,38,38,0.12)', label: t('contractForm.priorities.critical') },
  };
  const pCfg = priorityConfig[contract.priority] || priorityConfig.medium;

  // Confidentiality config
  const confidentialityConfig = {
    public: { color: '#10b981', bg: 'rgba(16,185,129,0.1)', label: t('contractForm.confidentialityLevels.public') },
    internal: { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', label: t('contractForm.confidentialityLevels.internal') },
    confidential: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: t('contractForm.confidentialityLevels.confidential') },
    restricted: { color: '#dc2626', bg: 'rgba(220,38,38,0.12)', label: t('contractForm.confidentialityLevels.restricted') },
  };
  const cCfg = confidentialityConfig[contract.confidentialityLevel] || confidentialityConfig.internal;

  // Compute contract duration
  const durationText = (() => {
    if (!contract.startDate || !contract.endDate) return null;
    const days = differenceInDays(new Date(contract.endDate), new Date(contract.startDate));
    if (days < 0) return null;
    if (days >= 365) return `${Math.round(days / 365 * 10) / 10} years`;
    if (days >= 30) return `${Math.round(days / 30)} months`;
    return `${days} days`;
  })();

  // Expiry progress
  const expiryProgress = (() => {
    if (!contract.startDate || !contract.endDate) return null;
    const total = differenceInDays(new Date(contract.endDate), new Date(contract.startDate));
    const elapsed = differenceInDays(new Date(), new Date(contract.startDate));
    if (total <= 0) return null;
    return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  })();

  return (
    <div className="cd">
      {/* ═══ Top Bar ═══ */}
      <div className="cd__topbar">
        {/* The back action honours where the contract was opened from: a task
            or notification list returns to its exact tab, everything else to
            the Contracts list. */}
        {(() => {
          const backTo = location.state?.backTo;
          const fromTasks = backTo?.path === '/approvals';
          return (
            <button
              className="cd__back"
              onClick={() => (fromTasks
                ? navigate(backTo.path, { state: { openTab: backTo.tab } })
                : navigate('/contracts'))}
            >
              <FiArrowLeft size={16} />
              <span>
                {fromTasks
                  ? (backTo.tab
                    ? t(`approvals.tabs.${backTo.tab}`, { defaultValue: t('approvals.title') })
                    : t('approvals.title'))
                  : t('contractDetail.contracts')}
              </span>
            </button>
          );
        })()}
      </div>

      {/* What the approver asked for — without it the initiator would be told
          to change something but not what. Shown to everyone on the contract,
          since the edits may well be made by someone else. */}
      {isPendingEdit && changeRequest && (
        <div className={`cd__changes-banner${wasRejected ? ' cd__changes-banner--rejected' : ''}`}>
          {wasRejected ? <FiX size={16} /> : <FiCornerUpLeft size={16} />}
          <div>
            <strong>
              {t(wasRejected ? 'contractDetail.rejectedAtStep' : 'contractDetail.changesRequestedAt', {
                step: localizeSeeded(changeRequest.stepName, i18n.language),
              })}
            </strong>
            {changeRequest.comments && <p>{changeRequest.comments}</p>}
            <span className="cd__changes-banner__meta">
              {changeRequest.requestedBy
                ? t(wasRejected ? 'contractDetail.rejectedByOn' : 'contractDetail.changesRequestedBy', {
                  name: `${changeRequest.requestedBy.firstName || ''} ${changeRequest.requestedBy.lastName || ''}`.trim(),
                  date: formatDateTime(changeRequest.requestedAt),
                })
                : formatDateTime(changeRequest.requestedAt)}
              {changeRequest.assignedTo && (
                <> · {t('contractDetail.nowWith', {
                  name: `${changeRequest.assignedTo.firstName || ''} ${changeRequest.assignedTo.lastName || ''}`.trim(),
                })}</>
              )}
            </span>
          </div>
        </div>
      )}


      {/* A superseded version is read-only history; a pending amendment/
          addendum is still working its way through approval. Both get a
          banner so nobody mistakes what they are looking at. */}
      {(isSuperseded || (isVersionContract && contract.versionState === 'pending')) && (
        <div className={`cd__version-banner ${isSuperseded ? 'cd__version-banner--superseded' : 'cd__version-banner--pending'}`}>
          <FiGitBranch size={16} />
          <div>
            <strong>
              {isSuperseded
                ? t('contractDetail.supersededBannerTitle', {
                  defaultValue: 'Previous version (V{{num}}) — read-only',
                  // ?? not || — V0 (the original) is a valid number here.
                  num: versionInfo?.majorVersion ?? contract.majorVersion ?? 0,
                })
                : t('contractDetail.pendingVersionBannerTitle', {
                  defaultValue: '{{type}} V{{num}} — not active until its approval process completes',
                  type: contract.versionType === 'addendum'
                    ? t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })
                    : t('contractDetail.updateTypes.amendment', { defaultValue: 'Amendment' }),
                  num: versionInfo?.majorVersion || contract.majorVersion,
                })}
            </strong>
            {contract.versionChangeNote && <p>{contract.versionChangeNote}</p>}
            {isSuperseded && currentFamilyVersion && currentFamilyVersion._id !== id && (
              <Link to={`/contracts/${currentFamilyVersion._id}`} className="cd__version-banner__link">
                {t('contractDetail.goToActiveVersion', { defaultValue: 'Go to the active version' })} →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ═══ Fixed bottom action dock — mirrors the contract form ═══ */}
      <div className="cd__dock" role="toolbar" aria-label={t('contractDetail.actions', { defaultValue: 'Contract actions' })}>
        <div className="cd__dock__inner">
          <div className="cd__dock__summary">
            {approvalProgress && approvalProgress.total > 0 ? (
              <div className="cd__dock__progress">
                <span className="cd__dock__progress-num">{approvalPercent}<i>%</i></span>
                <div className="cd__dock__progress-meta">
                  <span className="cd__dock__progress-label">
                    {approvalProgress.completed}/{approvalProgress.total} {t('contractDetail.stepsApproved', { defaultValue: 'steps approved' })}
                    {approvalProgress.currentStepName && !approvalProgress.rejected && (
                      <> · {localizeSeeded(approvalProgress.currentStepName, i18n.language)}</>
                    )}
                  </span>
                  <div className="cd__dock__progress-track">
                    <div
                      className={`cd__dock__progress-fill${approvalProgress.rejected ? ' cd__dock__progress-fill--rejected' : ''}`}
                      style={{ width: `${approvalPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <span className="cd__dock__status" style={{ color: statusConfig.color, background: statusConfig.bg }}>
                <StatusIcon size={13} />
                {statusConfig.label}
              </span>
            )}
          </div>

          {/* A closed process gets no actions — only the status it ended on.
              The rest of the page (overview, documents, timeline, history)
              stays exactly as it is, so the contract is still fully readable. */}
          {!processClosed && (
          <div className="cd__dock__actions">
          {canEdit && allowsUnlocked('canEditContract') && (
            <Link to={`/contracts/${id}/edit`} className="cd__action-btn cd__action-btn--outline">
              <FiEdit2 size={15} /> {t('contractDetail.edit')}
            </Link>
          )}
          {canRequestEdit && (
            <button
              className="cd__action-btn cd__action-btn--outline"
              onClick={handleRequestEdit}
              disabled={requestEditLoading}
            >
              <FiEdit2 size={15} /> {requestEditLoading ? t('common.processing') : t('contractDetail.requestEdit')}
            </button>
          )}
          {/* Update menu — only for contracts whose approval process is
              completed. Amendment/Addendum spawn a new contract version that
              re-runs the workflow; the other two change nothing structural. */}
          {canUpdateContract && (
            <div className="cd__sign-dropdown-wrap">
              <button
                className="cd__action-btn cd__action-btn--primary"
                onClick={() => setShowUpdateMenu(!showUpdateMenu)}
              >
                <FiRefreshCw size={15} /> {t('contractDetail.update', { defaultValue: 'Update' })} <FiChevronRight size={12} style={{ transform: showUpdateMenu ? 'rotate(90deg)' : 'none', transition: '0.15s' }} />
              </button>
              {showUpdateMenu && (
                <div className="cd__sign-dropdown">
                  {canAmend && (
                    <button onClick={() => openUpdateModal('amendment')}>
                      <FiGitBranch size={14} /> {t('contractDetail.updateTypes.amendment', { defaultValue: 'Amendment' })}
                    </button>
                  )}
                  {canAddendum && (
                    <button onClick={() => openUpdateModal('addendum')}>
                      <FiPlus size={14} /> {t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })}
                    </button>
                  )}
                  {canUpdateResponsible && (
                    <button onClick={() => openUpdateModal('responsible')}>
                      <FiUser size={14} /> {t('contractDetail.updateTypes.responsible', { defaultValue: 'Responsible Person Update' })}
                    </button>
                  )}
                  {canAddSupporting && (
                    <button onClick={() => openUpdateModal('supporting')}>
                      <FiPaperclip size={14} /> {t('contractDetail.updateTypes.supporting', { defaultValue: 'Add Supporting Document' })}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {canSubmitReview && contract.status === 'draft' && (
            <button className="cd__action-btn cd__action-btn--outline" onClick={() => openWorkflowPicker('review')}>
              <FiSend size={15} /> {t('contractDetail.submitForReview')}
            </button>
          )}
          {/* Start-approval is hidden while a review is active (in_review); it
              only returns once the review is finished (review_completed) or when
              the contract hasn't been sent for review yet (draft). */}
          {canEdit && (['draft', 'review_completed'].includes(contract.status)
            // Returned for edits but with no workflow recorded (a contract sent
            // back before that was stored): fall back to picking one manually,
            // so the contract is never stuck without a way forward.
            || (isPendingEdit && !changeRequest?.workflowId)) && (
            <button className="cd__action-btn cd__action-btn--primary" onClick={() => openWorkflowPicker('approval')}>
              <FiPlay size={15} /> {t('contractDetail.startApproval')}
            </button>
          )}
          {/* What the assignee may do here follows the step's type: a review
              step is only ever marked reviewed, an approval step carries the
              three decisions. Information steps never produce a task at all. */}
          {myPendingApproval && !isSignedStepTurn && isReviewStepTurn && (
            <button
              className="cd__action-btn cd__action-btn--approve"
              onClick={handleApprove}
              disabled={approvalActionLoading}
            >
              <FiCheckCircle size={15} /> {approvalActionLoading ? t('common.processing') : t('contractDetail.markReviewed')}
            </button>
          )}
          {myPendingApproval && !isSignedStepTurn && isControlStepTurn && (
            <>
              <button
                className="cd__action-btn cd__action-btn--approve"
                onClick={handleApprove}
                disabled={approvalActionLoading}
              >
                <FiCheckCircle size={15} /> {approvalActionLoading ? t('common.processing') : t('contractDetail.controlPass')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--reject"
                onClick={() => setShowRejectModal(true)}
                disabled={approvalActionLoading}
              >
                <FiX size={15} /> {t('contractDetail.controlFail')}
              </button>
            </>
          )}
          {myPendingApproval && !isSignedStepTurn && !isReviewStepTurn && !isControlStepTurn && (
            <>
              <button
                className="cd__action-btn cd__action-btn--approve"
                onClick={handleApprove}
                disabled={approvalActionLoading}
              >
                <FiCheckCircle size={15} /> {approvalActionLoading ? t('common.processing') : t('contractDetail.approve')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--changes"
                onClick={() => setShowChangesModal(true)}
                disabled={approvalActionLoading}
              >
                <FiCornerUpLeft size={15} /> {t('contractDetail.requestChanges')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--reject"
                onClick={() => setShowRejectModal(true)}
                disabled={approvalActionLoading}
              >
                <FiX size={15} /> {t('contractDetail.reject')}
              </button>
            </>
          )}
          {/* Görüş Al / Yönlendir — offered only where the workflow step's
              designer toggles allow them; the signed-contract system step
              never carries either. */}
          {myPendingApproval && !isSignedStepTurn && myPendingApproval.allowOpinion === true && (
            <button
              className="cd__action-btn cd__action-btn--outline"
              onClick={() => setShowOpinionModal(true)}
              disabled={approvalActionLoading}
            >
              <FiMessageSquare size={15} /> {t('workflowActions.opinion.title')}
            </button>
          )}
          {myPendingApproval && !isSignedStepTurn && myPendingApproval.allowForward === true && (
            <button
              className="cd__action-btn cd__action-btn--outline"
              onClick={() => setShowForwardModal(true)}
              disabled={approvalActionLoading}
            >
              <FiCornerUpRight size={15} /> {t('workflowActions.forward.title')}
            </button>
          )}
          {/* An opinion was requested from this user — the answer is written
              in the Görüş Bildir popup; the completed opinion then shows up
              in the Yorumlar feed as a "Görüş Verildi" entry. */}
          {myPendingOpinion && (
            <button
              className="cd__action-btn cd__action-btn--primary"
              onClick={() => setShowGiveOpinionModal(true)}
            >
              <FiMessageSquare size={15} /> {t('approvals.giveOpinion')}
            </button>
          )}
          {/* Initiator's side of a "request changes": edit (the button above,
              since pending_edit is editable) and then resend, which runs the
              same workflow again from its first step. */}
          {canResend && (
            <button
              className="cd__action-btn cd__action-btn--primary"
              onClick={handleResend}
              disabled={approvalActionLoading}
            >
              <FiSend size={15} /> {approvalActionLoading ? t('common.processing') : t('contractDetail.resend')}
            </button>
          )}
          {/* …or the holder lets it go: the request is cleared and the
              contract becomes a draft again. */}
          {canCancelChangeRequest && (
            <button
              className="cd__action-btn cd__action-btn--reject"
              onClick={handleCancelChangeRequest}
              disabled={approvalActionLoading}
            >
              <FiX size={15} /> {t('contractDetail.cancelChangeRequest')}
            </button>
          )}
          {/* Three separate cases, in the order they occur:
              — before a flow: the contract is the creator's to send;
              — inside a running flow: a step ability, offered only where the
                designer switched "Harici Gönder" on for that step;
              — sent back to its initiator: the flow-level "Geri Gönderimde
                Başlatana Harici Gönder İzni" switch decides, and with it off
                nobody is offered the action here — the creator included. */}
          {(canSendExternalOnReturn
            || (!isPendingEdit && isContractCreator && !isImmutable)
            || canSendExternalFromStep) && (
            <button className="cd__action-btn cd__action-btn--external" onClick={() => setShowExternalPartyModal(true)}>
              <FiExternalLink size={15} /> {t('contractDetail.sendToExternalParty')}
            </button>
          )}
          {/* Signed-contract step: the signing date is completed here, in
              place — the contract never goes back a stage for it. */}
          {canEditSigningDateNow && (
            <div className="cd__signdate">
              <span className="cd__signdate-label">
                <FiCalendar size={14} /> {t('contractForm.signingDate')}
              </span>
              <DateField
                value={signDateDraft ?? currentSigningDate}
                onChange={setSignDateDraft}
              />
              <button
                className="cd__action-btn cd__action-btn--primary cd__action-btn--sm"
                disabled={savingSignDate || signDateDraft == null || signDateDraft === currentSigningDate}
                onClick={handleSaveSigningDate}
              >
                {savingSignDate ? t('common.processing') : t('common.save')}
              </button>
            </div>
          )}
          {/* Signature-stage escape hatch: instead of signing, the workflow
              initiator can pull the contract back into revision — it becomes
              editable again and must re-run the approval flow afterwards. */}
          {isSignedStepTurn && contract.status === 'pending_approval' && (
            <button
              className="cd__action-btn cd__action-btn--changes"
              onClick={() => setShowReturnRevisionModal(true)}
              disabled={approvalActionLoading}
            >
              <FiCornerUpLeft size={15} /> {t('contractDetail.returnToRevision')}
            </button>
          )}
          {/* Sign button — for the creator once approved, and for whoever
              holds the workflow's final signed-contract step (signing IS
              that step; completing it finishes the process). */}
          {!isSuperseded && ((isContractCreator && contract.status === 'approved') || isSignedStepTurn) && (
            <div className="cd__sign-dropdown-wrap">
              <button
                className="cd__action-btn cd__action-btn--sign"
                onClick={() => setShowSignDropdown(!showSignDropdown)}
              >
                <FiEdit2 size={15} /> {t('contractDetail.sign')} <FiChevronRight size={12} style={{ transform: showSignDropdown ? 'rotate(90deg)' : 'none', transition: '0.15s' }} />
              </button>
              {showSignDropdown && (
                <div className="cd__sign-dropdown">
                  <button onClick={handleSignNormal}>
                    <FiEdit2 size={14} /> {t('contractDetail.signNormal')}
                  </button>
                  {hasDocuSign && (
                    <button onClick={handleSignDocuSign}>
                      <FiExternalLink size={14} /> {t('contractDetail.signViaDocuSign')}
                    </button>
                  )}
                  <button onClick={handleUploadSignedClick}>
                    <FiUpload size={14} /> {t('contractDetail.uploadSignedCopy', { defaultValue: 'Upload signed copy' })}
                  </button>
                </div>
              )}
              <input
                ref={signedUploadRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: 'none' }}
                onChange={handleSignedFileSelected}
              />
            </div>
          )}
          {/* Signing in progress indicator */}
          {signingStatus && ['in_progress', 'pending'].includes(signingStatus.status) && (() => {
            const isDocuSign = signingStatus.method === 'docusign';
            const totalSigners = (signingStatus.documents || []).reduce((sum, d) => sum + (d.signers?.length || 0), 0);
            const signedCount = (signingStatus.documents || []).reduce((sum, d) => sum + (d.signers?.filter(s => s.status === 'signed').length || 0), 0);
            return (
              <div className="cd__signing-status">
                <span className="cd__signing-badge">
                  <FiClock size={12} />
                  {isDocuSign
                    ? `${t('contractDetail.docuSignSigningInProgress')}${signingStatus.docusignTemplateName ? ` (${signingStatus.docusignTemplateName})` : ''}`
                    : `${t('contractDetail.signingInProgress')} (${signedCount}/${totalSigners})`
                  }
                </span>
                {isContractCreator && isDocuSign && (
                  <button className="cd__action-btn cd__action-btn--outline cd__action-btn--sm" onClick={handleCheckDocuSignStatus}>
                    <FiRefreshCw size={12} /> {t('contractDetail.checkStatus')}
                  </button>
                )}
                {isContractCreator && (
                  <button className="cd__action-btn cd__action-btn--outline cd__action-btn--sm" onClick={handleCancelSigning}>
                    {t('contractDetail.cancelSigning')}
                  </button>
                )}
              </div>
            );
          })()}
          </div>
          )}
        </div>
      </div>

      {/* ═══ Hero Section ═══ */}
      <div className="cd__hero">
        {/* Status + Priority Row */}
        <div className="cd__hero-badges">
          <span className="cd__status" style={{ color: statusConfig.color, background: statusConfig.bg }}>
            <StatusIcon size={13} />
            {statusConfig.label}
          </span>
          {/* Addendum records are flagged everywhere the contract appears —
              here on the detail page and in every list (Ek Protokol). */}
          {contract.versionType === 'addendum' && (
            <span className="badge badge-addendum">
              {t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })}
            </span>
          )}
          <span className="cd__priority" style={{ color: pCfg.color, background: pCfg.bg }}>
            <span className="cd__priority-dot" style={{ background: pCfg.color }} />
            {t('contractDetail.priorityLabel', { label: pCfg.label })}
          </span>
          <span className="cd__priority" style={{ color: cCfg.color, background: cCfg.bg }}>
            <span className="cd__priority-dot" style={{ background: cCfg.color }} />
            {cCfg.label}
          </span>
          {contract.autoRenewal && (
            <span className="cd__renewal-badge">
              <FiRefreshCw size={12} /> {t('contractDetail.autoRenewal')}
            </span>
          )}

          {/* Version switcher — replaces the old Versions tab. Right-aligned
              in the status row so the viewed version is always visible. */}
          {hasMajorVersions && (() => {
            const stateOf = (v) => (v.versionState === 'current'
              ? { label: t('contractDetail.versionActive', { defaultValue: 'Active' }), cls: 'is-active' }
              : v.versionState === 'superseded'
                ? { label: t('contractDetail.versionPrevious', { defaultValue: 'Previous version' }), cls: 'is-superseded' }
                : { label: getStatusConfig(v.status).label, cls: 'is-pending' });
            const typeLabelOf = (v) => (v.versionType === 'amendment'
              ? t('contractDetail.updateTypes.amendment', { defaultValue: 'Amendment' })
              : v.versionType === 'addendum'
                ? t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })
                : t('contractDetail.originalContract', { defaultValue: 'Original Contract' }));
            const viewing = contractVersions.find((v) => v._id === id);
            const viewingNum = viewing ? (viewing.majorVersion ?? 0) : (versionInfo?.majorVersion ?? 0);
            const viewingState = viewing
              ? stateOf(viewing)
              : stateOf({ versionState: versionInfo?.versionState, status: contract.status });
            return (
              <div className="cd__version-switch" ref={versionMenuRef}>
                <button
                  type="button"
                  className="cd__version-switch__btn"
                  onClick={() => setShowVersionMenu((s) => !s)}
                  aria-haspopup="listbox"
                  aria-expanded={showVersionMenu}
                >
                  <span className="cd__version-switch__label">{t('contractDetail.versionLabel', { defaultValue: 'Version' })}:</span>
                  <strong>V{viewingNum}</strong>
                  <FiChevronDown size={14} className={`cd__version-switch__caret ${showVersionMenu ? 'is-open' : ''}`} />
                </button>
                <span className={`cd__version-switch__state ${viewingState.cls}`}>{viewingState.label}</span>
                {showVersionMenu && (
                  <div className="cd__version-switch__menu" role="listbox">
                    {contractVersions.map((v) => {
                      const st = stateOf(v);
                      return (
                        <button
                          key={v._id}
                          type="button"
                          role="option"
                          aria-selected={v._id === id}
                          className={`cd__version-switch__item ${v._id === id ? 'is-viewing' : ''}`}
                          onClick={() => {
                            setShowVersionMenu(false);
                            if (v._id !== id) navigate(`/contracts/${v._id}`);
                          }}
                        >
                          <span className="cd__version-switch__item-main">
                            <strong>V{v.majorVersion ?? 0}</strong> — {typeLabelOf(v)}
                            <em>{v.contractNumber}</em>
                          </span>
                          <span className={`cd__version-switch__state ${st.cls}`}>{st.label}</span>
                        </button>
                      );
                    })}
                    {contractVersionsLoading && contractVersions.length === 0 && (
                      <span className="cd__version-switch__empty">{t('common.loading')}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Alert Banner */}
        {(isExpiringSoon || isExpired) && (
          <div className={`cd__alert ${isExpired ? 'cd__alert--danger' : 'cd__alert--warning'}`}>
            <FiAlertCircle size={16} />
            <span>
              {isExpired
                ? t('contractDetail.contractExpired', { days: Math.abs(daysUntilExpiry) })
                : t('contractDetail.contractExpiringSoon', { days: daysUntilExpiry })}
            </span>
          </div>
        )}

        {/* Duration Progress */}
        {expiryProgress !== null && (
          <div className="cd__timeline">
            <div className="cd__timeline-header">
              <span className="cd__timeline-label">{t('contractDetail.contractTimeline')}</span>
              {durationText && <span className="cd__timeline-duration">{durationText}</span>}
            </div>
            <div className="cd__timeline-bar">
              <div
                className={`cd__timeline-fill ${expiryProgress > 85 ? 'cd__timeline-fill--danger' : expiryProgress > 65 ? 'cd__timeline-fill--warning' : ''}`}
                style={{ width: `${expiryProgress}%` }}
              />
            </div>
            <div className="cd__timeline-dates">
              <span>{formatDate(contract.startDate)}</span>
              <span className="cd__timeline-pct">{t('contractDetail.elapsed', { percent: expiryProgress })}</span>
              <span>{formatDate(contract.endDate)}</span>
            </div>
          </div>
        )}
      </div>

      {/* ═══ Tab Navigation ═══ */}
      <div className="cd__tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`cd__tab ${activeTab === tab.id ? 'cd__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span className="cd__tab-label">{tab.label}</span>
            {tab.count > 0 && <span className="cd__tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* ═══ Tab Content ═══ */}
      <div className="cd__body">
        <div className="cd__tabpanel" key={activeTab}>

        {/* ── OVERVIEW TAB ── */}
        {/* Full width — the old Tags/Quick Stats sidebar column was removed;
            comments live in their own tab. */}
        {activeTab === 'overview' && (
          <ContractOverview contract={contract} fieldSchema={fieldSchema} />
        )}

        {/* ── ATTACHMENTS TAB ── */}
        {activeTab === 'attachments' && (
          <div className="cd__tab-content">
            <div className="cd__card">
              <div className="cd__card-head">
                <div className="cd__card-icon"><FiPaperclip size={16} /></div>
                <h3>{t('contractDetail.attachments')} ({contract.documents?.length || 0})</h3>
                {((canUpload && allowsUnlocked('canAddDocuments')) || opinionCanAddSupporting) && (
                  <label className="cd__action-btn cd__action-btn--primary" style={{ marginLeft: 'auto' }}>
                    <FiUpload size={15} /> {t('contractDetail.upload')}
                    <input type="file" style={{ display: 'none' }} onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg" />
                  </label>
                )}
              </div>
              <div className="cd__card-body">
                {(!contract.documents || contract.documents.length === 0) ? (
                  <div className="cd__empty-state">
                    <div className="cd__empty-icon"><FiPaperclip size={28} /></div>
                    <p>{t('contractDetail.noAttachmentsUploaded')}</p>
                    {((canUpload && allowsUnlocked('canAddDocuments')) || opinionCanAddSupporting) && (
                      <label className="cd__action-btn cd__action-btn--outline">
                        <FiUpload size={15} /> {t('contractDetail.uploadFirstAttachment')}
                        <input type="file" style={{ display: 'none' }} onChange={handleFileUpload}
                          accept=".pdf,.doc,.docx,.txt,.png,.jpg" />
                      </label>
                    )}
                  </div>
                ) : (
                  <div className="cd__doc-list">
                    {contract.documents.map((doc, i) => {
                      const expired = doc.expiresAt && new Date(doc.expiresAt) < new Date();
                      return (
                        <div key={doc._id || i} className="cd__doc-item">
                          <div className="cd__doc-item-icon"><FiFileText size={16} /></div>
                          <div className="cd__doc-item-info">
                            <span className="cd__doc-item-name">
                              {doc.originalName}
                              {doc.docTypeId?.name && (
                                <span className="cd__doc-tile-tag cd__doc-tile-tag--locked" style={{ marginLeft: 8 }}>
                                  {getLocalized(doc.docTypeId.name, i18n.language)}
                                </span>
                              )}
                            </span>
                            <span className="cd__doc-item-meta">
                              {(doc.size / 1024).toFixed(1)} KB
                              {doc.uploadedBy && ` · ${doc.uploadedBy.firstName || ''} ${doc.uploadedBy.lastName || ''}`.trimEnd()}
                              {doc.uploadedAt && ` · ${formatDateMedium(doc.uploadedAt)}`}
                            </span>
                            {doc.expiresAt && (
                              <span className={`cd__doc-item-meta${expired ? ' cd__doc-item-meta--danger' : ''}`}>
                                {t('contractDetail.supportingExpiresAt', { defaultValue: 'Expiry date' })}: {formatDateMedium(doc.expiresAt)}
                                {expired && ` · ${t('contractDetail.supportingExpired', { defaultValue: 'Expired' })}`}
                              </span>
                            )}
                          </div>
                          {canDownloadNow && (
                            <button className="cd__action-btn cd__action-btn--outline" onClick={() => handleDownload(doc)}>
                              <FiDownload size={15} /> {t('contractDetail.download')}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── DOCUMENTS TAB ── */}
        {activeTab === 'documents' && (
          <div className="cd__tab-content">
            <div className="cd__card">
              <input
                ref={versionUploadInputRef}
                type="file"
                accept=".docx,.doc"
                style={{ display: 'none' }}
                onChange={handleVersionFileSelected}
              />
              <div className="cd__card-head">
                <div className="cd__card-icon"><FiFileText size={16} /></div>
                <h3>{t('contractDetail.contractDocuments')} ({contractDocs.length})</h3>
                <div className="cd__doc-headactions">
                  {canEditDocumentsNow && (
                    <button className="cd__action-btn cd__action-btn--outline cd__action-btn--sm" onClick={() => navigate(`/contracts/${id}/documents`)}>
                      <FiExternalLink size={15} /> {t('contractDetail.openFullEditor')}
                    </button>
                  )}
                </div>
              </div>
              <div className="cd__card-body">
                {/* Draft upload — one dropzone, always open, identical for the
                    initiator at the first stage and for the approver whose
                    Control step gates on the draft. Gone once the contract has
                    its (single) draft document; changes go in as versions. */}
                {!contractDocsLoading && !hasDraftDoc && allowsUnlocked('canAddDocuments') && (
                  <div className="cd__doc-add">
                    <label
                      className={`cd__doc-drop${draftDragOver ? ' cd__doc-drop--active' : ''}${creatingDoc ? ' cd__doc-drop--busy' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setDraftDragOver(true); }}
                      onDragLeave={() => setDraftDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDraftDragOver(false);
                        handleDraftFilePicked(e.dataTransfer.files?.[0]);
                      }}
                    >
                      <span className="cd__doc-drop-icon"><FiUpload size={22} /></span>
                      <span className="cd__doc-drop-title">
                        {creatingDoc ? t('contractDetail.uploading') : t('contractDetail.draftDropTitle')}
                      </span>
                      <small>{t('contractDetail.draftDropHint')}</small>
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        accept=".doc,.docx"
                        disabled={creatingDoc}
                        onChange={(e) => {
                          const f = e.target.files[0];
                          e.target.value = '';
                          handleDraftFilePicked(f);
                        }}
                      />
                    </label>
                    {/* Starting from an empty document is the rarer path — kept
                        as a one-click aside so it costs the upload nothing. */}
                    {canEditDocumentsNow && (
                      <button
                        type="button"
                        className="cd__doc-blank"
                        disabled={creatingDoc}
                        onClick={() => createContractDoc(null)}
                      >
                        <FiFileText size={13} /> {t('contractDetail.startBlankInstead')}
                      </button>
                    )}
                  </div>
                )}

                {/* Documents — Windows-style icon grid */}
                {contractDocsLoading ? (
                  <div className="cd-loading" style={{ padding: '2rem' }}>
                    <div className="cd-loading__spinner" />
                    <span>{t('contractDetail.loadingDocuments')}</span>
                  </div>
                ) : contractDocs.length === 0 ? (
                  /* The dropzone above already says what to do — the empty
                     state is only for users who cannot add the draft. */
                  !hasDraftDoc && allowsUnlocked('canAddDocuments') ? null : (
                    <div className="cd__empty-state">
                      <div className="cd__empty-icon"><FiFileText size={28} /></div>
                      <p>{t('contractDetail.noDocumentsYet')}</p>
                    </div>
                  )
                ) : (() => {
                    // Two fixed groups (CLM): the draft contract with its
                    // version history, and the signed/final copy. A document
                    // belongs to exactly one of them, by its category.
                    const signedDocs = contractDocs.filter((d) => d.category === 'signed');
                    const draftDocs = contractDocs.filter((d) => d.category !== 'signed');
                    const renderDocCard = (doc) => {
                      const locked = doc.isLocked;
                      const signed = doc.category === 'signed';
                      const dv = docVersions[doc._id]?.versions || [];
                      // dv[0] is the newest version record. doc.currentVersion can
                      // lag behind it: the pointer is deliberately frozen while an
                      // editor session is open (OnlyOffice key stability), so the
                      // header shows the real latest version, not the pointer.
                      const latestVersion = dv[0]?.versionNumber || doc.currentVersion;
                      const latestAt = dv[0]?.createdAt || doc.updatedAt;
                      const isOpen = openVersionsDocId === doc._id;
                      // Editable → editor; locked / signed / no-edit-rights →
                      // the in-app read-only viewer (no download required).
                      const openViewer = (version = null) => setViewerTarget({
                        docId: doc._id,
                        title: docDisplayName(doc.title),
                        // Raw title: the download name appends its own version /
                        // lock marker, and the contract number goes after those.
                        baseTitle: doc.title,
                        version,
                        signed,
                        locked,
                      });
                      const onOpen = () => {
                        if (!locked && canEditDocumentsNow) {
                          navigate(`/contracts/${id}/documents/${doc._id}/editor`, { state: { from: 'contract' } });
                          return;
                        }
                        openViewer();
                      };
                      return (
                        <div key={doc._id} className={`cd__docfile${isOpen ? ' cd__docfile--open' : ''}`}>
                          <div className="cd__docfile-head">
                            <button type="button" className="cd__docfile-main" onClick={onOpen} title={docDisplayName(doc.title)}>
                              <span className="cd__docfile-art">
                                <CdFileGlyph doc={doc} />
                                {locked && <span className="cd__docfile-lock" aria-hidden="true"><FiLock /></span>}
                              </span>
                              <span className="cd__docfile-info">
                                <span className="cd__docfile-name">{docDisplayName(doc.title)}</span>
                                <span className="cd__docfile-meta">
                                  v{latestVersion}{latestAt && ` · ${formatRelative(latestAt)}`}
                                </span>
                              </span>
                            </button>

                            <div className="cd__docfile-actions">
                              {locked ? (
                                <>
                                  <span className={`cd__doc-tile-tag ${signed ? 'cd__doc-tile-tag--signed' : 'cd__doc-tile-tag--locked'}`}>
                                    {signed ? t('contractDetail.signed') : t('contractDetail.locked')}
                                  </span>
                                  <button type="button" className="cd__docfile-btn" onClick={() => openViewer()}>
                                    <FiEye size={12} /> {t('contractDetail.viewDocument', { defaultValue: 'View' })}
                                  </button>
                                </>
                              ) : canEditDocumentsNow ? (
                                <button type="button" className="cd__docfile-btn" onClick={onOpen}>
                                  <FiEdit2 size={12} /> {t('contractDetail.openEditor')}
                                </button>
                              ) : (
                                <button type="button" className="cd__docfile-btn" onClick={() => openViewer()}>
                                  <FiEye size={12} /> {t('contractDetail.viewDocument', { defaultValue: 'View' })}
                                </button>
                              )}

                              {/* Header download serves the current version — same
                                  permission gate as the per-version download rows. */}
                              {canDownloadNow && (
                                <button
                                  type="button"
                                  className="cd__docfile-btn"
                                  onClick={() => handleDownloadDocVersion(doc, latestVersion)}
                                >
                                  <FiDownload size={12} /> {t('contractDetail.download')}
                                </button>
                              )}

                              {dv.length >= 2 && (
                                <Link to={`/contracts/${id}/documents/${doc._id}/compare`} className="cd__docfile-btn">
                                  <FiGitBranch size={12} /> {t('contractDetail.compareVersions')}
                                </Link>
                              )}

                              {/* A step grant suffices — the flow designer handed the
                                  assignee this action; no base permission demanded.
                                  Likewise a Görüş Al grant, for the opinion giver. */}
                              {(((canUpload || reviewerActions?.canUploadVersions === true)
                                && allowsUnlocked('canUploadVersions'))
                                || opinionCanUploadVersions) && (
                                <button
                                  type="button"
                                  className="cd__docfile-btn"
                                  disabled={uploadingVersionDocId === doc._id}
                                  onClick={() => triggerVersionUpload(doc._id)}
                                >
                                  <FiUpload size={12} />
                                  {uploadingVersionDocId === doc._id
                                    ? t('contractDetail.uploading')
                                    : t('contractDetail.uploadNewVersion')}
                                </button>
                              )}

                              <button
                                type="button"
                                className="cd__docfile-toggle"
                                aria-expanded={isOpen}
                                onClick={() => setOpenVersionsDocId(isOpen ? null : doc._id)}
                              >
                                <FiClock size={12} />
                                {t('contractDetail.versionCount', { count: dv.length })}
                                <FiChevronDown className={`cd__docfile-chevron${isOpen ? ' cd__docfile-chevron--open' : ''}`} />
                              </button>
                            </div>
                          </div>

                          {/* QR verification result — shown permanently under the signed copy */}
                          {signed && doc.qrVerification?.status && (
                            <div className={`cd__qr-verify cd__qr-verify--${doc.qrVerification.status}`}>
                              <span className="cd__qr-verify-status">
                                {doc.qrVerification.status === 'matched'
                                  ? <FiCheckCircle size={13} />
                                  : <FiAlertCircle size={13} />}
                                {t('qrVerification.title', { defaultValue: 'QR Verification' })}:{' '}
                                {t(`qrVerification.${doc.qrVerification.status === 'not_verified' ? 'notVerified' : doc.qrVerification.status}`, {
                                  defaultValue: doc.qrVerification.status,
                                })}
                              </span>
                              {doc.qrVerification.detail && doc.qrVerification.detail !== 'active_version' && (
                                <span className="cd__qr-verify-desc">
                                  {t(`qrVerification.details.${doc.qrVerification.detail}`, { defaultValue: '' })}
                                </span>
                              )}
                              {doc.qrVerification.overrideReason && (
                                <span className="cd__qr-verify-override">
                                  {t('qrVerification.overrideBy', { defaultValue: 'Verification Override' })}: {doc.qrVerification.overrideByName || '—'}
                                  {' · '}{t('qrVerification.overrideReason', { defaultValue: 'Reason' })}: {doc.qrVerification.overrideReason}
                                  {doc.qrVerification.overrideAt && <>{' · '}{formatDateTime(doc.qrVerification.overrideAt)}</>}
                                </span>
                              )}
                            </div>
                          )}

                          {isOpen && (
                            <div className="cd__docfile-versions">
                              {docVersionsLoading && dv.length === 0 ? (
                                <div className="cd-loading" style={{ padding: '1.25rem' }}>
                                  <div className="cd-loading__spinner" />
                                  <span>{t('contractDetail.loadingDocumentVersions')}</span>
                                </div>
                              ) : dv.length === 0 ? (
                                <p className="cd__doc-version-empty">{t('contractDetail.noVersionsRecorded')}</p>
                              ) : (
                                <div className="cd__timeline-list">
                                  {dv.map((v, i) => (
                                    <div key={v._id} className="cd__timeline-item">
                                      <div className="cd__timeline-track">
                                        <div className={`cd__timeline-dot ${i === 0 ? 'cd__timeline-dot--current' : ''}`} />
                                        {i < dv.length - 1 && <div className="cd__timeline-line" />}
                                      </div>
                                      <div className="cd__timeline-content">
                                        <div className="cd__timeline-head">
                                          <span className="cd__timeline-version">v{v.versionNumber}</span>
                                          {i === 0 && <span className="cd__current-badge">{t('contractDetail.current')}</span>}
                                          <span className="cd__timeline-date">{formatRelative(v.createdAt)}</span>
                                        </div>
                                        {v.changeSummary && <p className="cd__timeline-desc">{v.changeSummary}</p>}
                                        <div className="cd__timeline-meta">
                                          {v.createdBy && (
                                            <span className="cd__timeline-author">
                                              <FiUser size={12} /> {v.createdBy.firstName || 'Unknown'} {v.createdBy.lastName || ''}
                                            </span>
                                          )}
                                          {v.fileSize && (
                                            <span className="cd__timeline-size">
                                              {v.fileSize >= 1048576
                                                ? `${(v.fileSize / 1048576).toFixed(1)} MB`
                                                : `${Math.round(v.fileSize / 1024)} KB`}
                                            </span>
                                          )}
                                          {/* Historical versions open read-only in the viewer;
                                              the current row serves the authoritative file. */}
                                          <button
                                            type="button"
                                            className="cd__timeline-download"
                                            onClick={() => openViewer(i === 0 ? null : v.versionNumber)}
                                          >
                                            <FiEye size={12} /> {t('contractDetail.viewDocument', { defaultValue: 'View' })}
                                          </button>
                                          {canDownloadNow && (
                                            <button
                                              type="button"
                                              className="cd__timeline-download"
                                              onClick={() => handleDownloadDocVersion(doc, v.versionNumber)}
                                            >
                                              <FiDownload size={12} /> {t('contractDetail.download')}
                                            </button>
                                          )}
                                          {i < dv.length - 1 && (
                                            <Link
                                              to={`/contracts/${id}/documents/${doc._id}/compare?versions=${dv[i + 1].versionNumber},${v.versionNumber}`}
                                              className="cd__timeline-compare"
                                            >
                                              <FiGitBranch size={12} /> {t('contractDetail.compareWithPrevious', { n: dv[i + 1].versionNumber })}
                                            </Link>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    };
                    return (
                      <div className="cd__docgroups">
                        <section className="cd__docgroup">
                          <h4 className="cd__docgroup-title">
                            <FiFileText size={14} /> {t('contractDetail.draftContract')}
                            <span className="cd__docgroup-count">{draftDocs.length}</span>
                          </h4>
                          {draftDocs.length === 0 ? (
                            <p className="cd__docgroup-empty">{t('contractDetail.noDraftDocs')}</p>
                          ) : (
                            <div className="cd__docfiles">{draftDocs.map(renderDocCard)}</div>
                          )}
                        </section>
                        <section className="cd__docgroup">
                          <h4 className="cd__docgroup-title cd__docgroup-title--signed">
                            <FiEdit2 size={14} /> {t('contractDetail.signedContract')}
                            <span className="cd__docgroup-count">{signedDocs.length}</span>
                          </h4>
                          {signedDocs.length === 0 ? (
                            <p className="cd__docgroup-empty">{t('contractDetail.noSignedDocs')}</p>
                          ) : (
                            <div className="cd__docfiles">{signedDocs.map(renderDocCard)}</div>
                          )}
                        </section>
                      </div>
                    );
                  })()}
              </div>
            </div>
          </div>
        )}

        {/* ── COMMENTS TAB ── */}
        {activeTab === 'comments' && (
          <div className="cd__tab-content">
            <div className="cd__card">
              <div className="cd__card-head">
                <div className="cd__card-icon cd__card-icon--blue"><FiMessageSquare size={16} /></div>
                <h3>{t('contractDetail.comments')} ({commentFeed.length})</h3>
              </div>
              <div className="cd__card-body">
                {/* Comment Form */}
                <form className="cd__comment-form" onSubmit={handleAddComment}>
                  <div className="cd__avatar cd__avatar--lg">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <div className="cd__comment-form-body">
                    <textarea
                      placeholder={t('contractDetail.addComment')}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={3}
                    />
                    <div className="cd__comment-form-footer">
                      <button
                        type="submit"
                        className="cd__action-btn cd__action-btn--primary"
                        disabled={submittingComment || !newComment.trim()}
                      >
                        {submittingComment ? t('contractDetail.posting') : t('contractDetail.postComment')}
                      </button>
                    </div>
                  </div>
                </form>

                {/* Comments + Görüş entries in one feed */}
                {commentFeed.length === 0 ? (
                  <div className="cd__empty-state">
                    <div className="cd__empty-icon"><FiMessageSquare size={28} /></div>
                    <p>{t('contractDetail.noCommentsYet')}</p>
                  </div>
                ) : (
                  <div className="cd__comment-list">
                    {commentFeed.map((item) => {
                      if (item.kind === 'opinion_request') {
                        const o = item.o;
                        const requesterName = `${o.requestedBy?.firstName || ''} ${o.requestedBy?.lastName || ''}`.trim();
                        const targetName = `${o.assignee?.firstName || ''} ${o.assignee?.lastName || ''}`.trim();
                        const reqInitials = `${o.requestedBy?.firstName?.[0] || ''}${o.requestedBy?.lastName?.[0] || ''}`;
                        // Read-only on purpose: the addressee answers through
                        // the Görüş Bildir popup in the action bar, so this
                        // entry is a record of the request, not a second way
                        // to act on it.
                        return (
                          <div key={`opreq-${o._id}`} className="cd__comment">
                            <div className="cd__avatar">{reqInitials}</div>
                            <div className="cd__comment-body">
                              <div className="cd__comment-header">
                                <span className="cd__comment-name">{requesterName}</span>
                                <span className="cd__opinion-status cd__opinion-status--pending">
                                  {t('contractDetail.opinionEntry.requested')}
                                </span>
                                {o.status === 'cancelled' && (
                                  <span className="cd__opinion-status cd__opinion-status--cancelled">
                                    {t('contractDetail.opinionStatus.cancelled')}
                                  </span>
                                )}
                                <span className="cd__comment-time">{formatRelative(o.createdAt)}</span>
                              </div>
                              <p className="cd__opinion-meta">
                                {t('contractDetail.opinionAskedTo', {
                                  name: targetName,
                                  step: localizeSeeded(o.stepName, i18n.language),
                                })}
                                {o.departmentId?.name ? ` · ${o.departmentId.name}` : ''}
                              </p>
                              {o.requestNote && <p className="cd__comment-text">{o.requestNote}</p>}
                            </div>
                          </div>
                        );
                      }
                      if (item.kind === 'opinion_answer') {
                        const o = item.o;
                        const giverName = `${o.assignee?.firstName || ''} ${o.assignee?.lastName || ''}`.trim();
                        const giverInitials = `${o.assignee?.firstName?.[0] || ''}${o.assignee?.lastName?.[0] || ''}`;
                        return (
                          <div key={`opans-${o._id}`} className="cd__comment">
                            <div className="cd__avatar">{giverInitials}</div>
                            <div className="cd__comment-body">
                              <div className="cd__comment-header">
                                <span className="cd__comment-name">{giverName}</span>
                                <span className="cd__opinion-status cd__opinion-status--answered">
                                  {t('contractDetail.opinionEntry.given')}
                                </span>
                                <span className="cd__comment-time">{formatRelative(o.respondedAt || o.updatedAt)}</span>
                              </div>
                              <p className="cd__opinion-meta">
                                {localizeSeeded(o.stepName, i18n.language)}
                              </p>
                              <p className="cd__comment-text">{o.opinion}</p>
                            </div>
                          </div>
                        );
                      }
                      const c = item.c;
                      const isExternal = !!c.externalAuthorName;
                      const authorName = isExternal
                        ? c.externalAuthorName
                        : `${c.userId?.firstName || ''} ${c.userId?.lastName || ''}`.trim();
                      const initials = isExternal
                        ? c.externalAuthorName.split(' ').map(w => w[0]).slice(0, 2).join('')
                        : `${c.userId?.firstName?.[0] || ''}${c.userId?.lastName?.[0] || ''}`;
                      return (
                        <div key={c._id} className="cd__comment">
                          <div className={`cd__avatar ${isExternal ? 'cd__avatar--external' : ''}`}>
                            {initials}
                          </div>
                          <div className="cd__comment-body">
                            <div className="cd__comment-header">
                              <span className="cd__comment-name">{authorName}</span>
                              {isExternal && (
                                <span className="cd__comment-external-badge">
                                  <FiGlobe size={10} /> {t('contractDetail.externalParty')}
                                </span>
                              )}
                              <span className="cd__comment-time">{formatRelative(c.createdAt)}</span>
                            </div>
                            <p className="cd__comment-text">{c.content}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── HISTORY TAB ── */}
        {activeTab === 'history' && (
          <div className="cd__tab-content">
            {/* Contract Versions */}
            <div className="cd__card">
              <div className="cd__card-head">
                <div className="cd__card-icon"><FiClock size={16} /></div>
                <h3>{t('contractDetail.contractVersionHistory')} ({versions?.length || 0})</h3>
              </div>
              <div className="cd__card-body">
                {(!versions || versions.length === 0) ? (
                  <div className="cd__empty-state">
                    <div className="cd__empty-icon"><FiClock size={28} /></div>
                    <p>{t('contractDetail.noVersionHistory')}</p>
                  </div>
                ) : (
                  <div className="cd__timeline-list">
                    {versions.map((v, i) => (
                      <div key={v._id} className="cd__timeline-item">
                        <div className="cd__timeline-track">
                          <div className={`cd__timeline-dot ${i === 0 ? 'cd__timeline-dot--current' : ''}`} />
                          {i < versions.length - 1 && <div className="cd__timeline-line" />}
                        </div>
                        <div className="cd__timeline-content">
                          <div className="cd__timeline-head">
                            <span className="cd__timeline-version">{t('contractDetail.changeNumber', { num: v.version })}</span>
                            {i === 0 && <span className="cd__current-badge">{t('contractDetail.current')}</span>}
                            <span className="cd__timeline-date">{formatRelative(v.createdAt)}</span>
                          </div>
                          {v.changeDescription && <p className="cd__timeline-desc">{localizeSystemText(v.changeDescription, t, i18n.language)}</p>}
                          {v.changes?.length > 0 && (
                            <ul className="cd__changes">
                              {v.changes.map((c, idx) => (
                                <li key={idx} className="cd__change">
                                  <span className="cd__change-field">{changeFieldLabel(c)}</span>
                                  <span className="cd__change-flow">
                                    <span className="cd__change-from">{formatChangeValue(c.field, c.from)}</span>
                                    <FiChevronRight size={11} className="cd__change-arrow" />
                                    <span className="cd__change-to">{formatChangeValue(c.field, c.to)}</span>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {v.changedBy && (
                            <span className="cd__timeline-author">
                              <FiUser size={12} /> {v.changedBy.firstName || 'Unknown'} {v.changedBy.lastName || ''}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── TIMELINE TAB ── */}
        {activeTab === 'timeline' && (
          <div className="cd__tab-content">
            <ContractTimeline contractId={id} />
          </div>
        )}

        {/* ── VIEWS TAB — who opened this contract and when ── */}
        {activeTab === 'views' && (
          <div className="cd__tab-content">
            <div className="cd__card">
              <div className="cd__card-head">
                <div className="cd__card-icon"><FiEye size={16} /></div>
                <h3>{t('contractDetail.viewHistory')} ({viewHistory.length})</h3>
              </div>
              <div className="cd__card-body">
                {viewHistoryLoading ? (
                  <div className="cd__empty-state">
                    <div className="cd__empty-icon"><FiEye size={28} /></div>
                    <p>{t('common.loading')}</p>
                  </div>
                ) : viewHistory.length === 0 ? (
                  <div className="cd__empty-state">
                    <div className="cd__empty-icon"><FiEye size={28} /></div>
                    <p>{t('contractDetail.noViewsYet')}</p>
                  </div>
                ) : (
                  <div className="cd__views-list">
                    {viewHistory.map((v) => (
                      <div key={v._id} className="cd__view-row">
                        <div className="cd__avatar">
                          {v.userId?.firstName?.[0]}{v.userId?.lastName?.[0]}
                        </div>
                        <div className="cd__view-info">
                          <span className="cd__view-name">
                            {v.userId
                              ? `${v.userId.firstName || ''} ${v.userId.lastName || ''}`.trim() || t('contractDetail.unknownUser')
                              : t('contractDetail.unknownUser')}
                            {v.impersonatorId && (
                              <span className="cd__view-impersonated">
                                {t('contractDetail.viewedViaImpersonation', {
                                  name: `${v.impersonatorId.firstName || ''} ${v.impersonatorId.lastName || ''}`.trim(),
                                })}
                              </span>
                            )}
                            {v.delegateId && (
                              <span className="cd__view-impersonated">
                                {t('contractDetail.viewedViaDelegation', {
                                  name: `${v.delegateId.firstName || ''} ${v.delegateId.lastName || ''}`.trim(),
                                })}
                              </span>
                            )}
                          </span>
                          {v.userId?.email && <span className="cd__view-email">{v.userId.email}</span>}
                        </div>
                        <div className="cd__view-time">
                          <span className="cd__view-time-abs">{formatDateTime(v.viewedAt)}</span>
                          <span className="cd__view-time-rel">{formatRelative(v.viewedAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* ═══ External Party Modal ═══ */}
      {showExternalPartyModal && (
        <div className="cd__modal-overlay" onClick={() => setShowExternalPartyModal(false)}>
          <div className="cd__modal cd__modal--external" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--external">
                <FiExternalLink size={20} />
              </div>
              <div>
                <h2>{t('contractDetail.sendToExternalPartyTitle')}</h2>
                <p className="cd__modal-subtitle">{t('contractDetail.sendToExternalPartyDesc')}</p>
              </div>
              <button className="cd__modal-close" onClick={() => setShowExternalPartyModal(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              <div className="cd__ext-info-banner">
                <FiAlertCircle size={16} />
                {/* SEC-10: plain text — the string carries no markup, so no HTML injection is needed. */}
                <span>{t('contractDetail.externalPartyInfoBanner')}</span>
              </div>
              <div className="cd__ext-form">
                <div className="cd__ext-field">
                  <label htmlFor="ext-name">
                    <FiUser size={14} />
                    {t('contractDetail.fullName')}
                  </label>
                  <input
                    id="ext-name"
                    type="text"
                    placeholder={t('contractDetail.externalNamePlaceholder')}
                    value={externalPartyName}
                    onChange={(e) => setExternalPartyName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="cd__ext-field">
                  <label htmlFor="ext-email">
                    <FiSend size={14} />
                    {t('contractDetail.emailAddress')}
                  </label>
                  <input
                    id="ext-email"
                    type="email"
                    placeholder={t('contractDetail.externalEmailPlaceholder')}
                    value={externalPartyEmail}
                    onChange={(e) => setExternalPartyEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendToExternalParty()}
                  />
                </div>
              </div>
            </div>
            <div className="cd__modal-footer">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setShowExternalPartyModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--external"
                onClick={handleSendToExternalParty}
                disabled={sendingExternalInvite || !externalPartyName.trim() || !externalPartyEmail.trim()}
              >
                {sendingExternalInvite ? (
                  <><div className="cd__btn-spinner" /> {t('contractDetail.sending')}</>
                ) : (
                  <><FiSend size={14} /> {t('contractDetail.sendInvitation')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Workflow Picker Modal ═══ */}
      {showWorkflowPicker && (() => {
        const isReviewMode = workflowPickerMode === 'review';
        const closePicker = () => { setShowWorkflowPicker(false); setWorkflowPickerMode(null); setWfSearch(''); };
        // In review mode, only show workflows that have review-type steps
        const filteredWorkflows = isReviewMode
          ? workflows.filter(wf => wf.steps?.some(s => s.type === 'review'))
          : workflows;
        const q = wfSearch.trim().toLowerCase();
        const searchedWorkflows = q
          ? filteredWorkflows.filter(wf =>
              localizeSeeded(wf.name, i18n.language).toLowerCase().includes(q) ||
              wf.description?.toLowerCase().includes(q) ||
              wf.steps?.some(s => s.name?.toLowerCase().includes(q)))
          : filteredWorkflows;
        return (
        <div className="cd__modal-overlay" onClick={closePicker}>
          <div className="cd__modal cd__modal--wide cd__wfpick" onClick={(e) => e.stopPropagation()}>
            <button className="cd__modal-close cd__wfpick__close" onClick={closePicker}>
              <FiX size={16} />
            </button>
            <div className="cd__wfpick__grid">
              {/* ── Left: animation + intro ── */}
              <aside className="cd__wfpick__aside">
                <div className="cd__wfpick__lottie">
                  <Lottie animationData={searchingAnimation} loop autoplay />
                </div>
                <h3 className="cd__wfpick__title">
                  {isReviewMode ? t('contractDetail.selectReviewWorkflow', 'Select Review Workflow') : t('contractDetail.selectWorkflow')}
                </h3>
                <p className="cd__wfpick__desc">
                  {isReviewMode
                    ? t('contractDetail.selectReviewWorkflowDesc', { title: contract.contractNumber, defaultValue: `Choose a workflow to send "${contract.contractNumber}" for review. Only review steps will be activated.` })
                    : t('contractDetail.selectWorkflowDesc', { title: contract.contractNumber })}
                </p>
              </aside>

              {/* ── Right: search + flows ── */}
              <div className="cd__wfpick__main">
                {filteredWorkflows.length > 0 && (
                  <div className="cd__wfpick__search">
                    <FiSearch size={15} className="cd__wfpick__search-icon" />
                    <input
                      className="cd__wfpick__search-input"
                      placeholder={t('contractDetail.searchWorkflows', { defaultValue: 'Search workflows…' })}
                      value={wfSearch}
                      onChange={(e) => setWfSearch(e.target.value)}
                      autoFocus
                    />
                    {wfSearch && (
                      <button className="cd__wfpick__search-clear" onClick={() => setWfSearch('')}>
                        <FiX size={13} />
                      </button>
                    )}
                  </div>
                )}

                <div className="cd__wfpick__list">
                  {filteredWorkflows.length === 0 ? (
                    <div className="cd__empty-state">
                      <div className="cd__empty-icon">{isReviewMode ? <FiSend size={28} /> : <FiPlay size={28} />}</div>
                      <p>
                        {isReviewMode
                          ? t('contractDetail.noReviewWorkflows', 'No workflows with review steps found. ')
                          : t('contractDetail.noWorkflows')}{' '}
                        <Link to="/flow-management">{t('contractDetail.createWorkflowFirst')}</Link>.
                      </p>
                    </div>
                  ) : searchedWorkflows.length === 0 ? (
                    <div className="cd__empty-state">
                      <div className="cd__empty-icon"><FiSearch size={26} /></div>
                      <p>{t('contractDetail.noWorkflowMatches', { defaultValue: 'No workflows match your search.' })}</p>
                    </div>
                  ) : (
                    <div className="cd__wf-list">
                      {searchedWorkflows.map((wf) => {
                        const displaySteps = isReviewMode
                          ? wf.steps?.filter(s => s.type === 'review')
                          : wf.steps;
                        return (
                        <div
                          key={wf._id}
                          className="cd__wf-option"
                          onClick={() => !startingWorkflow && handleStartWorkflow(wf._id)}
                        >
                          <div className="cd__wf-icon">{isReviewMode ? <FiSend size={16} /> : <FiPlay size={16} />}</div>
                          <div className="cd__wf-info">
                            <strong>{localizeSeeded(wf.name, i18n.language)}</strong>
                            {wf.description && <p>{wf.description}</p>}
                            <div className="cd__wf-steps">
                              {displaySteps?.length} {isReviewMode ? 'review' : ''} step{displaySteps?.length !== 1 ? 's' : ''}: {displaySteps?.map(s => s.name).join(' → ')}
                            </div>
                          </div>
                          <button className="cd__action-btn cd__action-btn--primary" disabled={startingWorkflow}>
                            {startingWorkflow ? '...' : isReviewMode ? t('contractDetail.submit', 'Submit') : t('contractDetail.start')}
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="cd__wfpick__footer">
                  <button className="cd__action-btn cd__action-btn--outline" onClick={closePicker}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ═══ Signing Wizard (Normal Sign) ═══ */}
      {showSignWizard && (
        <div className="cd__modal-overlay" onClick={() => setShowSignWizard(false)}>
          <div className="cd__modal cd__modal--wide" onClick={(e) => e.stopPropagation()}>
            {/* Wizard Header */}
            <div className="cd__modal-head">
              <div>
                <h3>{t('contractDetail.signContract')}</h3>
                <p>{contract.contractName || contract.contractNumber}</p>
              </div>
              <button className="cd__modal-close" onClick={() => setShowSignWizard(false)}>
                <FiX size={16} />
              </button>
            </div>

            {/* Wizard Progress */}
            <div className="cd__wizard-progress">
              <div className={`cd__wizard-step ${wizardStep === 0 ? 'cd__wizard-step--active' : wizardStep > 0 ? 'cd__wizard-step--done' : ''}`}>
                <span className="cd__wizard-step-num">1</span>
                <span className="cd__wizard-step-label">{t('contractDetail.selectDocuments')}</span>
              </div>
              <div className="cd__wizard-step-line" />
              <div className={`cd__wizard-step ${wizardStep === 1 ? 'cd__wizard-step--active' : wizardStep > 1 ? 'cd__wizard-step--done' : ''}`}>
                <span className="cd__wizard-step-num">2</span>
                <span className="cd__wizard-step-label">{t('contractDetail.assignSigners')}</span>
              </div>
              <div className="cd__wizard-step-line" />
              <div className={`cd__wizard-step ${wizardStep === 2 ? 'cd__wizard-step--active' : ''}`}>
                <span className="cd__wizard-step-num">3</span>
                <span className="cd__wizard-step-label">{t('contractDetail.reviewAndSend')}</span>
              </div>
            </div>

            <div className="cd__modal-body">
              {/* ── Step 0: Select Documents ── */}
              {wizardStep === 0 && (
                <div className="cd__wizard-docs">
                  {lockedDocsLoading ? (
                    <div className="cd-loading" style={{ padding: '2rem' }}>
                      <div className="cd-loading__spinner" />
                      <span>{t('contractDetail.loadingLockedDocuments')}</span>
                    </div>
                  ) : lockedDocs.length === 0 ? (
                    <div className="cd__empty-state">
                      <div className="cd__empty-icon"><FiFileText size={28} /></div>
                      <p>{t('contractDetail.noLockedDocuments')}</p>
                    </div>
                  ) : (
                    <>
                      <p className="cd__wizard-hint">{t('contractDetail.selectDocumentsForSignature')}</p>
                      <div className="cd__wizard-doc-list">
                        {lockedDocs.map(doc => (
                          <label key={doc._id} className={`cd__wizard-doc-item ${selectedDocIds.includes(doc._id) ? 'cd__wizard-doc-item--selected' : ''}`}>
                            <input
                              type="checkbox"
                              checked={selectedDocIds.includes(doc._id)}
                              onChange={() => handleToggleDoc(doc._id)}
                            />
                            <FiFileText size={18} />
                            <div className="cd__wizard-doc-info">
                              <strong>{doc.title}</strong>
                              <span>{t('contractDetail.version', { num: doc.currentVersion })}</span>
                            </div>
                            <FiCheckCircle size={16} className="cd__wizard-doc-check" />
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Step 1: Assign Signers per Document ── */}
              {wizardStep === 1 && currentWizardDocId && (
                <div className="cd__wizard-signers">
                  {/* Document tabs */}
                  <div className="cd__wizard-doc-tabs">
                    {selectedDocIds.map((docId, idx) => (
                      <button
                        key={docId}
                        className={`cd__wizard-doc-tab ${idx === activeWizardDocIndex ? 'cd__wizard-doc-tab--active' : ''} ${docSignerMap[docId]?.signers?.every(s => s.name && s.email) ? 'cd__wizard-doc-tab--complete' : ''}`}
                        onClick={() => { setActiveWizardDocIndex(idx); setActiveSignerIndex(null); setUserSearchQuery(''); }}
                      >
                        <span className="cd__wizard-doc-tab-num">{idx + 1}</span>
                        {getDocTitle(docId)}
                      </button>
                    ))}
                  </div>

                  <div className="cd__wizard-signer-header">
                    <h4>
                      <FiFileText size={15} /> {getDocTitle(currentWizardDocId)}
                    </h4>
                    <span className="cd__wizard-doc-counter">
                      {t('contractDetail.documentNofM', { current: activeWizardDocIndex + 1, total: selectedDocIds.length })}
                    </span>
                  </div>

                  {/* Signing order */}
                  <div className="cd__sign-order">
                    <label>{t('contractDetail.signingOrder')}</label>
                    <SearchableSelect
                      value={currentDocOrder}
                      onChange={(v) => handleWizardSigningOrderChange(v || 'parallel')}
                      options={[
                        {
                          value: 'parallel',
                          label: t('contractDetail.parallelLabel'),
                          sublabel: t('contractDetail.parallelAllAtOnce'),
                        },
                        {
                          value: 'sequential',
                          label: t('contractDetail.sequentialLabel'),
                          sublabel: t('contractDetail.sequentialInOrder'),
                        },
                      ]}
                    />
                  </div>

                  {/* Signer rows */}
                  <div className="cd__signer-list">
                    {currentDocSigners.map((signer, idx) => (
                      <div key={idx} className="cd__signer-row">
                        <span className="cd__signer-num">{idx + 1}</span>
                        <div className="cd__signer-fields">
                          <div className="cd__signer-field-group">
                            <input
                              type="text"
                              placeholder={t('contractDetail.fullNamePlaceholder')}
                              value={signer.name}
                              onChange={(e) => handleWizardSignerChange(idx, 'name', e.target.value)}
                            />
                            <input
                              type="email"
                              placeholder={t('contractDetail.emailPlaceholder')}
                              value={signer.email}
                              onChange={(e) => handleWizardSignerChange(idx, 'email', e.target.value)}
                            />
                          </div>
                          <div className="cd__signer-search">
                            <button
                              className="cd__signer-search-btn"
                              onClick={() => { setActiveSignerIndex(activeSignerIndex === idx ? null : idx); setUserSearchQuery(''); }}
                            >
                              <FiUser size={13} /> {t('contractDetail.searchUsers')}
                            </button>
                            {activeSignerIndex === idx && (
                              <div className="cd__signer-search-dropdown">
                                <input
                                  type="text"
                                  placeholder={t('contractDetail.searchByNameOrEmail')}
                                  value={userSearchQuery}
                                  onChange={(e) => setUserSearchQuery(e.target.value)}
                                  autoFocus
                                />
                                {userSearchResults.length > 0 && (
                                  <div className="cd__signer-search-results">
                                    {userSearchResults.map(u => (
                                      <button key={u._id} onClick={() => handleSelectUser(idx, u)}>
                                        <strong>{u.firstName} {u.lastName}</strong>
                                        <span>{u.email}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {currentDocSigners.length > 1 && (
                          <button className="cd__signer-remove" onClick={() => handleWizardRemoveSigner(idx)}>
                            <FiX size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button className="cd__signer-add" onClick={handleWizardAddSigner}>
                    <FiPlus size={15} /> {t('contractDetail.addSigner')}
                  </button>
                </div>
              )}

              {/* ── Step 2: Review ── */}
              {wizardStep === 2 && (
                <div className="cd__wizard-review">
                  <p className="cd__wizard-hint">{t('contractDetail.reviewSigningConfig')}</p>
                  <div className="cd__wizard-review-list">
                    {selectedDocIds.map((docId, idx) => {
                      const entry = docSignerMap[docId];
                      return (
                        <div key={docId} className="cd__wizard-review-doc">
                          <div className="cd__wizard-review-doc-head">
                            <FiFileText size={16} />
                            <strong>{getDocTitle(docId)}</strong>
                            <span className="cd__wizard-review-order">{entry.signingOrder === 'sequential' ? t('contractDetail.sequentialLabel') : t('contractDetail.parallelLabel')}</span>
                          </div>
                          <div className="cd__wizard-review-signers">
                            {entry.signers.map((s, si) => (
                              <div key={si} className="cd__wizard-review-signer">
                                <span className="cd__wizard-review-signer-num">{si + 1}</span>
                                <div>
                                  <strong>{s.name}</strong>
                                  <span>{s.email}</span>
                                </div>
                                {s.userId && <span className="cd__wizard-review-internal">{t('contractDetail.internal')}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Wizard Footer */}
            <div className="cd__modal-footer">
              {wizardStep === 0 && (
                <>
                  <button className="cd__action-btn cd__action-btn--outline" onClick={() => setShowSignWizard(false)}>
                    {t('common.cancel')}
                  </button>
                  <button
                    className="cd__action-btn cd__action-btn--primary"
                    onClick={handleWizardNextFromDocs}
                    disabled={selectedDocIds.length === 0 || lockedDocsLoading}
                  >
                    {t('contractDetail.nextAssignSigners')} <FiChevronRight size={15} />
                  </button>
                </>
              )}
              {wizardStep === 1 && (
                <>
                  <button className="cd__action-btn cd__action-btn--outline" onClick={handleWizardPrevDoc}>
                    <FiArrowLeft size={15} /> {activeWizardDocIndex === 0 ? t('contractDetail.backToDocuments') : t('contractDetail.previousDocument')}
                  </button>
                  <button className="cd__action-btn cd__action-btn--primary" onClick={handleWizardNextDoc}>
                    {activeWizardDocIndex < selectedDocIds.length - 1 ? (
                      <>{t('contractDetail.nextDocument')} <FiChevronRight size={15} /></>
                    ) : (
                      <>{t('contractDetail.review')} <FiChevronRight size={15} /></>
                    )}
                  </button>
                </>
              )}
              {wizardStep === 2 && (
                <>
                  <button className="cd__action-btn cd__action-btn--outline" onClick={() => { setWizardStep(1); setActiveWizardDocIndex(selectedDocIds.length - 1); }}>
                    <FiArrowLeft size={15} /> {t('common.back')}
                  </button>
                  <button
                    className="cd__action-btn cd__action-btn--sign"
                    onClick={handleSubmitNormalSigning}
                    disabled={sendingSigning}
                  >
                    {sendingSigning ? (
                      <><div className="cd__btn-spinner" /> {t('contractDetail.sending')}</>
                    ) : (
                      <><FiSend size={14} /> {t('contractDetail.sendForSigning')}</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ DocuSign Signing Modal ═══ */}
      {showDocuSignModal && (
        <div className="cd__modal-overlay" onClick={() => setShowDocuSignModal(false)}>
          <div className="cd__modal cd__modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-head">
              <div>
                <h3>{t('contractDetail.signViaDocuSignTitle')}</h3>
                <p>{t('contractDetail.selectTemplateAndSigners')}</p>
              </div>
              <button className="cd__modal-close" onClick={() => setShowDocuSignModal(false)}>
                <FiX size={16} />
              </button>
            </div>
            <div className="cd__modal-body">
              {dsTemplatesLoading ? (
                <div className="cd-loading"><div className="cd-loading__spinner" /></div>
              ) : !dsSelectedTemplate ? (
                <>
                  <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#64748b' }}>{t('contractDetail.selectDocuSignTemplate')}</p>
                  {dsTemplates.length === 0 ? (
                    <div className="cd__empty-state">
                      <div className="cd__empty-icon"><FiFileText size={28} /></div>
                      <p>{t('contractDetail.noDocuSignTemplates')}</p>
                    </div>
                  ) : (
                    <div className="cd__wf-list">
                      {dsTemplates.map(tmpl => (
                        <div key={tmpl.templateId} className="cd__wf-option" onClick={() => handleSelectDsTemplate(tmpl)}>
                          <div className="cd__wf-icon"><FiFileText size={16} /></div>
                          <div className="cd__wf-info">
                            <strong>{tmpl.name}</strong>
                            {tmpl.description && <p>{tmpl.description}</p>}
                            <div className="cd__wf-steps">{t('contractDetail.signerRoles', { count: tmpl.roles?.length || 0 })}</div>
                          </div>
                          <button className="cd__action-btn cd__action-btn--primary">{t('contractDetail.select')}</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <button className="cd__action-btn cd__action-btn--outline cd__action-btn--sm" onClick={() => setDsSelectedTemplate(null)}>
                      <FiArrowLeft size={12} /> {t('common.back')}
                    </button>
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>{dsSelectedTemplate.name}</span>
                  </div>
                  <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#64748b' }}>{t('contractDetail.configureSigners')}</p>
                  <div className="cd__signer-list">
                    {dsSigners.map((signer, idx) => (
                      <div key={idx} className="cd__signer-row">
                        <span className="cd__signer-num" style={{ minWidth: 80 }}>{signer.roleName}</span>
                        <div className="cd__signer-fields">
                          <div className="cd__signer-field-group">
                            <input
                              type="text"
                              placeholder={t('contractDetail.fullNamePlaceholder')}
                              value={signer.name}
                              onChange={(e) => handleDsSignerChange(idx, 'name', e.target.value)}
                            />
                            <input
                              type="email"
                              placeholder={t('contractDetail.emailPlaceholder')}
                              value={signer.email}
                              onChange={(e) => handleDsSignerChange(idx, 'email', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="cd__modal-footer">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setShowDocuSignModal(false)}>
                {t('common.cancel')}
              </button>
              {dsSelectedTemplate && (
                <button
                  className="cd__action-btn cd__action-btn--sign"
                  onClick={handleSubmitDocuSign}
                  disabled={dsSending}
                >
                  {dsSending ? (
                    <><div className="cd__btn-spinner" /> {t('contractDetail.sending')}</>
                  ) : (
                    <><FiSend size={14} /> {t('contractDetail.sendForSigning')}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Signed copy upload confirmation ═══ */}
      <ConfirmDialog
        open={!!confirmSignedFile}
        variant="primary"
        icon={FiUpload}
        title={t('contractDetail.confirmMarkExecutedTitle', { defaultValue: 'İmzalı Kopyayı Yükle' })}
        message={t('contractDetail.confirmMarkExecuted', { defaultValue: 'Upload this signed copy and mark the contract as fully executed? This cannot be undone.' })}
        confirmLabel={t('contractDetail.uploadSignedCopy', { defaultValue: 'Upload signed copy' })}
        loading={uploadingSigned}
        onConfirm={() => handleConfirmSignedUpload()}
        onCancel={() => setConfirmSignedFile(null)}
      />

      {/* ═══ In-app read-only document viewer ═══ */}
      {viewerTarget && (
        <DocumentViewer
          open
          contractId={id}
          documentId={viewerTarget.docId}
          versionNumber={viewerTarget.version}
          title={viewerTarget.title}
          badge={viewerTarget.signed ? 'signed' : viewerTarget.locked ? 'locked' : null}
          showSignature={viewerTarget.signed}
          allowDownload={canDownloadNow}
          downloadName={`${docDisplayName(`${viewerTarget.baseTitle || viewerTarget.title}${viewerTarget.version ? `_v${viewerTarget.version}` : viewerTarget.signed ? '_signed' : viewerTarget.locked ? '_LockVersion' : ''}`)}.pdf`}
          onClose={() => setViewerTarget(null)}
        />
      )}

      {/* ═══ QR verification failed — continue with a mandatory reason ═══ */}
      {qrFailure && (
        <div className="cd__modal-overlay" onClick={() => { if (!uploadingSigned) { setQrFailure(null); setQrOverrideReason(''); } }}>
          <div className="cd__modal cd__modal--changes" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--changes">
                <FiAlertCircle size={20} />
              </div>
              <div>
                <h2>{t('qrVerification.failedModalTitle', { defaultValue: 'QR Verification Failed' })}</h2>
                <p className="cd__modal-subtitle">
                  {t(`qrVerification.details.${qrFailure.detail || 'qr_not_found'}`, {
                    defaultValue: t('qrVerification.failedModalDesc', {
                      defaultValue: 'The uploaded document could not be verified against the approved draft.',
                    }),
                  })}
                </p>
              </div>
              <button className="cd__modal-close" onClick={() => { setQrFailure(null); setQrOverrideReason(''); }}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              {qrFailure.matchedContract?.id && (
                <p className="cd__modal-subtitle" style={{ margin: '0 0 10px' }}>
                  {t('qrVerification.belongsTo', { defaultValue: 'The QR code in the document belongs to:' })}{' '}
                  <Link
                    to={`/contracts/${qrFailure.matchedContract.id}`}
                    onClick={() => { setQrFailure(null); setQrOverrideReason(''); }}
                    style={{ fontWeight: 600 }}
                  >
                    {qrFailure.matchedContract.contractNumber || t('qrVerification.otherContract', { defaultValue: 'View contract' })}
                    {qrFailure.matchedContract.contractName ? ` — ${qrFailure.matchedContract.contractName}` : ''}
                  </Link>
                </p>
              )}
              <div className="cd__form-group">
                <label className="cd__form-label">
                  {t('qrVerification.overridePrompt', {
                    defaultValue: 'QR verification failed. To continue with the upload, enter a reason.',
                  })} <span className="cd__required">*</span>
                </label>
                <textarea
                  className="cd__form-textarea"
                  rows={3}
                  value={qrOverrideReason}
                  onChange={(e) => setQrOverrideReason(e.target.value)}
                  placeholder={t('qrVerification.reasonPlaceholder', {
                    defaultValue: 'e.g. The counterparty signed a different version of the document…',
                  })}
                  autoFocus
                />
              </div>
            </div>
            <div className="cd__modal-actions">
              <button
                className="cd__action-btn cd__action-btn--outline"
                onClick={() => { setQrFailure(null); setQrOverrideReason(''); }}
                disabled={uploadingSigned}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                className="cd__action-btn cd__action-btn--primary"
                onClick={() => performSignedUpload(qrFailure.file, qrOverrideReason.trim())}
                disabled={uploadingSigned || !qrOverrideReason.trim() || !qrFailure.file}
              >
                {uploadingSigned ? (
                  <><div className="cd__btn-spinner" /> {t('common.processing', { defaultValue: 'Processing…' })}</>
                ) : (
                  t('qrVerification.continueUpload', { defaultValue: 'Continue upload' })
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Reject Modal ═══ */}
      {showChangesModal && (
        <div className="cd__modal-overlay" onClick={() => setShowChangesModal(false)}>
          <div className="cd__modal cd__modal--changes" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--changes">
                <FiCornerUpLeft size={20} />
              </div>
              <div>
                <h2>{t('contractDetail.requestChanges')}</h2>
                <p className="cd__modal-subtitle">{t('contractDetail.requestChangesDesc')}</p>
              </div>
              <button className="cd__modal-close" onClick={() => setShowChangesModal(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              <div className="cd__form-group">
                <label className="cd__form-label">
                  {t('contractDetail.changesNeeded')} <span className="cd__required">*</span>
                </label>
                <textarea
                  className="cd__form-textarea"
                  rows={4}
                  value={changesComment}
                  onChange={(e) => setChangesComment(e.target.value)}
                  placeholder={t('contractDetail.changesPlaceholder')}
                />
              </div>
            </div>
            <div className="cd__modal-actions">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setShowChangesModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--changes"
                onClick={handleRequestChanges}
                disabled={approvalActionLoading}
              >
                <FiCornerUpLeft size={15} /> {approvalActionLoading ? t('common.processing') : t('contractDetail.sendBackForChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Return to Revision Modal (signature stage) ═══ */}
      {showReturnRevisionModal && (
        <div className="cd__modal-overlay" onClick={() => setShowReturnRevisionModal(false)}>
          <div className="cd__modal cd__modal--changes" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--changes">
                <FiCornerUpLeft size={20} />
              </div>
              <div>
                <h2>{t('contractDetail.returnToRevisionTitle')}</h2>
                <p className="cd__modal-subtitle">{t('contractDetail.returnToRevisionDesc')}</p>
              </div>
              <button className="cd__modal-close" onClick={() => setShowReturnRevisionModal(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              <div className="cd__form-group">
                <label className="cd__form-label">
                  {t('contractDetail.returnToRevisionReason')} <span className="cd__required">*</span>
                </label>
                <textarea
                  className="cd__form-textarea"
                  rows={4}
                  value={returnRevisionReason}
                  onChange={(e) => setReturnRevisionReason(e.target.value)}
                  placeholder={t('contractDetail.returnToRevisionPlaceholder')}
                />
              </div>
            </div>
            <div className="cd__modal-actions">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setShowReturnRevisionModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--changes"
                onClick={handleReturnToRevision}
                disabled={approvalActionLoading}
              >
                <FiCornerUpLeft size={15} /> {approvalActionLoading ? t('common.processing') : t('contractDetail.returnToRevision')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="cd__modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="cd__modal cd__modal--reject" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--reject">
                <FiX size={20} />
              </div>
              <div>
                {/* Control steps wear their own wording — the mechanics are a
                    rejection (the run ends), the decision is "Uygun Görülmedi". */}
                <h2>{isControlStepTurn ? t('contractDetail.controlFail') : t('contractDetail.rejectContract')}</h2>
                <p className="cd__modal-subtitle">{isControlStepTurn ? t('contractDetail.controlFailDesc') : t('contractDetail.rejectContractDesc')}</p>
              </div>
              <button className="cd__modal-close" onClick={() => setShowRejectModal(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              <div className="cd__form-group">
                <label className="cd__form-label">{isControlStepTurn ? t('contractDetail.controlFailReason') : t('contractDetail.reasonForRejection')}</label>
                <textarea
                  className="cd__form-textarea"
                  rows={4}
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  placeholder={isControlStepTurn ? t('contractDetail.controlFailPlaceholder') : t('contractDetail.rejectionPlaceholder')}
                />
              </div>
            </div>
            <div className="cd__modal-actions">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setShowRejectModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--reject"
                onClick={handleReject}
                disabled={approvalActionLoading}
              >
                <FiX size={15} /> {approvalActionLoading
                  ? t('common.processing')
                  : (isControlStepTurn ? t('contractDetail.controlFail') : t('contractDetail.reject'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Görüş Bildir Modal — the opinion is written and submitted here;
          the completed answer then appears in the Yorumlar feed ═══ */}
      {showGiveOpinionModal && myPendingOpinion && (
        <div className="cd__modal-overlay" onClick={() => setShowGiveOpinionModal(false)}>
          <div className="cd__modal" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--opinion">
                <FiMessageSquare size={20} />
              </div>
              <div>
                <h2>{t('approvals.actionTitles.giveOpinion')}</h2>
                <p className="cd__modal-subtitle">
                  {`${myPendingOpinion.requestedBy?.firstName || ''} ${myPendingOpinion.requestedBy?.lastName || ''}`.trim()}
                  {' · '}
                  {localizeSeeded(myPendingOpinion.stepName, i18n.language)}
                </p>
              </div>
              <button className="cd__modal-close" onClick={() => setShowGiveOpinionModal(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              {/* The requester's note — what they want assessed. */}
              {myPendingOpinion.requestNote && (
                <p className="cd__opinion-note">{myPendingOpinion.requestNote}</p>
              )}
              <div className="cd__form-group">
                <label className="cd__form-label">{t('approvals.actionCommentLabels.giveOpinion')}</label>
                <textarea
                  className="cd__form-textarea"
                  rows={4}
                  value={opinionDraft}
                  onChange={(e) => setOpinionDraft(e.target.value)}
                  placeholder={t('approvals.actionCommentPlaceholders.giveOpinion')}
                  maxLength={5000}
                  autoFocus
                />
              </div>
            </div>
            <div className="cd__modal-actions">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setShowGiveOpinionModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--primary"
                onClick={() => handleSubmitOpinion(myPendingOpinion)}
                disabled={opinionSubmitting || !opinionDraft.trim()}
              >
                <FiMessageSquare size={15} /> {opinionSubmitting ? t('common.processing') : t('approvals.actionConfirms.giveOpinion')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Amendment / Addendum Modal ═══ */}
      {(updateModal === 'amendment' || updateModal === 'addendum') && (
        <div className="cd__modal-overlay" onClick={() => setUpdateModal(null)}>
          <div className="cd__modal cd__modal--changes" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--changes">
                <FiGitBranch size={20} />
              </div>
              <div>
                <h2>
                  {updateModal === 'addendum'
                    ? t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })
                    : t('contractDetail.updateTypes.amendment', { defaultValue: 'Amendment' })}
                </h2>
                <p className="cd__modal-subtitle">
                  {t('contractDetail.versionModalDesc', {
                    defaultValue: 'Creates a new contract version based on the current one. The version goes through the contract\'s workflow from the beginning; the active contract stays unchanged until it completes.',
                  })}
                </p>
              </div>
              <button className="cd__modal-close" onClick={() => setUpdateModal(null)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              <div className="cd__version-locked-fields">
                <div className="cd__version-locked-field">
                  <span className="cd__form-label">{t('contractForm.contractType', { defaultValue: 'Contract Type' })} <FiLock size={11} /></span>
                  <span>{(() => { const n = contract.contractTypeId?.name; return typeof n === 'object' ? (n?.[i18n.language] || n?.en || n?.tr || '—') : (n || '—'); })()}</span>
                </div>
                <div className="cd__version-locked-field">
                  <span className="cd__form-label">{t('contractForm.counterparty', { defaultValue: 'Counterparty' })} <FiLock size={11} /></span>
                  <span>{contract.counterpartyName || '—'}</span>
                </div>
              </div>
              <p className="cd__version-locked-note">
                {t('contractDetail.identityLockedNote', {
                  defaultValue: 'Contract Type and Counterparty are carried over from the current contract and cannot be changed.',
                })}
              </p>
              <div className="cd__form-group">
                <label className="cd__form-label">
                  {t('contractDetail.changeLabel', { defaultValue: 'Change' })} <span className="cd__required">*</span>
                </label>
                <textarea
                  className="cd__form-textarea"
                  rows={4}
                  value={updateChangeNote}
                  onChange={(e) => setUpdateChangeNote(e.target.value)}
                  placeholder={t('contractDetail.changePlaceholder', {
                    defaultValue: 'Describe what this update changes — e.g. contract end date updated to 31.12.2026.',
                  })}
                />
              </div>
            </div>
            <div className="cd__modal-actions">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setUpdateModal(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--primary"
                onClick={handleCreateVersion}
                disabled={updateLoading}
              >
                <FiGitBranch size={15} /> {updateLoading ? t('common.processing') : t('contractDetail.createVersion', { defaultValue: 'Create new version' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Responsible Person Update Modal ═══ */}
      {updateModal === 'responsible' && (
        <div className="cd__modal-overlay" onClick={() => setUpdateModal(null)}>
          <div className="cd__modal cd__modal--changes" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--changes">
                <FiUser size={20} />
              </div>
              <div>
                <h2>{t('contractDetail.updateTypes.responsible', { defaultValue: 'Responsible Person Update' })}</h2>
                <p className="cd__modal-subtitle">
                  {t('contractDetail.responsibleModalDesc', {
                    defaultValue: 'Changes only the responsible person. No new version is created and the workflow does not restart.',
                  })}
                </p>
              </div>
              <button className="cd__modal-close" onClick={() => setUpdateModal(null)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              <div className="cd__form-group">
                <label className="cd__form-label">{t('contractDetail.currentResponsible', { defaultValue: 'Current responsible' })}</label>
                <div className="cd__resp-current">
                  <div className="cd__avatar">
                    {contract.responsibleUserId?.firstName?.[0]}{contract.responsibleUserId?.lastName?.[0]}
                  </div>
                  <div>
                    <span className="cd__resp-name">
                      {contract.responsibleUserId
                        ? `${contract.responsibleUserId.firstName || ''} ${contract.responsibleUserId.lastName || ''}`.trim()
                        : '—'}
                    </span>
                    {contract.responsibleUserId?.email && <span className="cd__resp-email">{contract.responsibleUserId.email}</span>}
                  </div>
                </div>
              </div>
              <div className="cd__form-group">
                <label className="cd__form-label">
                  {t('contractDetail.newResponsible', { defaultValue: 'New responsible' })} <span className="cd__required">*</span>
                </label>
                {respSelected ? (
                  <div className="cd__resp-current cd__resp-current--selected">
                    <div className="cd__avatar">{respSelected.firstName?.[0]}{respSelected.lastName?.[0]}</div>
                    <div>
                      <span className="cd__resp-name">{respSelected.firstName} {respSelected.lastName}</span>
                      {respSelected.email && <span className="cd__resp-email">{respSelected.email}</span>}
                    </div>
                    <button className="cd__modal-close" onClick={() => setRespSelected(null)} title={t('common.cancel')}>
                      <FiX size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="cd__resp-search">
                      <FiSearch size={14} />
                      <input
                        type="text"
                        value={respQuery}
                        onChange={(e) => setRespQuery(e.target.value)}
                        placeholder={t('contractDetail.searchUserPlaceholder', { defaultValue: 'Search by name or email…' })}
                      />
                    </div>
                    {respResults.length > 0 && (
                      <div className="cd__resp-results">
                        {respResults.map((u) => (
                          <button key={u._id} type="button" onClick={() => { setRespSelected(u); setRespQuery(''); setRespResults([]); }}>
                            <div className="cd__avatar">{u.firstName?.[0]}{u.lastName?.[0]}</div>
                            <div>
                              <span className="cd__resp-name">{u.firstName} {u.lastName}</span>
                              {u.email && <span className="cd__resp-email">{u.email}</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="cd__form-group">
                <label className="cd__form-label">
                  {t('contractDetail.changeLabel', { defaultValue: 'Change' })} <span className="cd__required">*</span>
                </label>
                <textarea
                  className="cd__form-textarea"
                  rows={3}
                  value={updateChangeNote}
                  onChange={(e) => setUpdateChangeNote(e.target.value)}
                  placeholder={t('contractDetail.responsibleChangePlaceholder', { defaultValue: 'Why is the responsible person changing?' })}
                />
              </div>
            </div>
            <div className="cd__modal-actions">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setUpdateModal(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--primary"
                onClick={handleResponsibleUpdate}
                disabled={updateLoading}
              >
                <FiUser size={15} /> {updateLoading ? t('common.processing') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Add Supporting Document Modal ═══ */}
      {updateModal === 'supporting' && (
        <div className="cd__modal-overlay" onClick={() => setUpdateModal(null)}>
          <div className="cd__modal cd__modal--changes" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--changes">
                <FiPaperclip size={20} />
              </div>
              <div>
                <h2>{t('contractDetail.updateTypes.supporting', { defaultValue: 'Add Supporting Document' })}</h2>
                <p className="cd__modal-subtitle">
                  {t('contractDetail.supportingModalDesc', {
                    defaultValue: 'Adds supporting documents to the completed contract. Existing documents are kept; no new version is created and the workflow does not restart.',
                  })}
                </p>
              </div>
              <button className="cd__modal-close" onClick={() => setUpdateModal(null)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              <div className="cd__form-group">
                <label className="cd__action-btn cd__action-btn--outline" style={{ cursor: 'pointer' }}>
                  <FiUpload size={15} /> {t('contractDetail.selectFiles', { defaultValue: 'Select files' })}
                  <input
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.xlsx,.xls"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) {
                        setSupportingFiles((prev) => [
                          ...prev,
                          ...files.map((f) => ({ file: f, docTypeId: '', expiresAt: '' })),
                        ]);
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {supportingFiles.length > 0 && (
                <div className="cd__doc-list">
                  {supportingFiles.map((row, i) => (
                    <div key={`${row.file.name}-${i}`} className="cd__doc-item cd__doc-item--stacked">
                      <div className="cd__doc-item-row">
                        <div className="cd__doc-item-icon"><FiFileText size={16} /></div>
                        <div className="cd__doc-item-info">
                          <span className="cd__doc-item-name">{row.file.name}</span>
                          <span className="cd__doc-item-meta">{Math.round(row.file.size / 1024)} KB</span>
                        </div>
                        <button
                          className="cd__modal-close"
                          onClick={() => setSupportingFiles((prev) => prev.filter((_, idx) => idx !== i))}
                          title={t('common.cancel')}
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                      <div className="cd__doc-item-fields">
                        <div className="cd__form-group">
                          <label>{t('contractDetail.supportingDocType', { defaultValue: 'Supporting document type' })}{supportingDocTypes.length > 0 && ' *'}</label>
                          <SearchableSelect
                            value={row.docTypeId}
                            onChange={(v) => setSupportingFiles((prev) => prev.map((r, idx) => (idx === i ? { ...r, docTypeId: v } : r)))}
                            placeholder={t('contractDetail.selectSupportingDocType', { defaultValue: 'Select type…' })}
                            // The type's code is internal bookkeeping — the picker shows the name only.
                            options={supportingDocTypes.map((dt) => ({ value: dt._id, label: getLocalized(dt.name, i18n.language) }))}
                          />
                        </div>
                        <div className="cd__form-group">
                          <label>{t('contractDetail.supportingExpiresAt', { defaultValue: 'Expiry date' })}</label>
                          <DateField
                            value={row.expiresAt}
                            onChange={(v) => setSupportingFiles((prev) => prev.map((r, idx) => (idx === i ? { ...r, expiresAt: v } : r)))}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="cd__modal-actions">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setUpdateModal(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--primary"
                onClick={handleSupportingUpload}
                disabled={updateLoading || supportingFiles.length === 0}
              >
                <FiUpload size={15} /> {updateLoading ? t('common.processing') : t('contractDetail.addDocuments', { defaultValue: 'Add documents' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Attachments-tab upload: type + expiry before the upload ═══ */}
      {attachStage && (
        <div className="cd__modal-overlay" onClick={() => setAttachStage(null)}>
          <div className="cd__modal cd__modal--changes" onClick={(e) => e.stopPropagation()}>
            <div className="cd__modal-header">
              <div className="cd__modal-header-icon cd__modal-header-icon--changes">
                <FiPaperclip size={20} />
              </div>
              <div>
                <h2>{t('contractDetail.uploadSupportingDoc', { defaultValue: 'Upload Supporting Document' })}</h2>
                <p className="cd__modal-subtitle">{attachStage.file.name} · {Math.round(attachStage.file.size / 1024)} KB</p>
              </div>
              <button className="cd__modal-close" onClick={() => setAttachStage(null)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="cd__modal-body">
              <div className="cd__form-group">
                <label>{t('contractDetail.supportingDocType', { defaultValue: 'Supporting document type' })}{supportingDocTypes.length > 0 && ' *'}</label>
                <SearchableSelect
                  value={attachStage.docTypeId}
                  onChange={(v) => setAttachStage((prev) => ({ ...prev, docTypeId: v }))}
                  placeholder={t('contractDetail.selectSupportingDocType', { defaultValue: 'Select type…' })}
                  // The type's code is internal bookkeeping — the picker shows the name only.
                  options={supportingDocTypes.map((dt) => ({ value: dt._id, label: getLocalized(dt.name, i18n.language) }))}
                />
              </div>
              <div className="cd__form-group">
                <label>{t('contractDetail.supportingExpiresAt', { defaultValue: 'Expiry date' })}</label>
                <DateField
                  value={attachStage.expiresAt}
                  onChange={(v) => setAttachStage((prev) => ({ ...prev, expiresAt: v }))}
                />
              </div>
            </div>
            <div className="cd__modal-actions">
              <button className="cd__action-btn cd__action-btn--outline" onClick={() => setAttachStage(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="cd__action-btn cd__action-btn--primary"
                onClick={handleAttachStageUpload}
                disabled={attachUploading}
              >
                <FiUpload size={15} /> {attachUploading ? t('common.processing') : t('contractDetail.upload')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Görüş Al — request opinions on my active step. Like every other
          process action, the view jumps to the Timeline so the request is
          seen landing in the history. */}
      <OpinionRequestModal
        open={showOpinionModal}
        onClose={() => setShowOpinionModal(false)}
        approval={myPendingApproval}
        onDone={() => { loadOpinions(); showTimelineAfterAction(); }}
      />

      {/* Yönlendir — hand my active action to another user; afterwards the
          contract is refetched so the dock stops offering owner actions. */}
      <ForwardActionModal
        open={showForwardModal}
        onClose={() => setShowForwardModal(false)}
        approval={myPendingApproval}
        onDone={() => { dispatch(fetchContract(id)); showTimelineAfterAction(); }}
      />
    </div>
  );
}

// ─── Detail Item Sub-component ───────────────────────────────────────────────
function DetailItem({ icon, label, children, highlight, danger, full }) {
  return (
    <div className={`cd__detail-item ${full ? 'cd__detail-item--full' : ''}`}>
      <span className="cd__detail-label">
        {icon}
        {label}
      </span>
      <span className={`cd__detail-value ${highlight ? 'cd__detail-value--highlight' : ''} ${danger ? 'cd__detail-value--danger' : ''}`}>
        {children}
      </span>
    </div>
  );
}

export default ContractDetail;
